import axios from "axios"
import accessToken from "./jwt-token-access/accessToken"
import config from "../config/app.config"
import {GET_TASKS_OF_ORDER,SHIPPING, GET_BANKS, SALESPERSONS,PAYMENTS,GET_CODES,POST_LOGIN, GET_ORDERS, GET_TASKS, GET_ALL_TASKS, GET_CUSTOMERS, CREATE_CUSTOMER, GET_PRODUCTS, CREATE_PRODUCT, CREATE_ORDER, CREATE_TASK, GET_TYPES,GET_PROVIDERS,INVOICES,CREATE_LOAN, VEHICLES, COMPANY_DETAILS, SYSTEM_USERS, SYSTEM_ROLES, SYSTEM_APPS, DEFAULT_APP_ID, API_URL, APP_NAME, FLEXFLOW, INVOICE_COMPANIES, VAT_CATEGORIES, QUOTES} from "./api_urls"

// Get API key from config
const APP_API_KEY = config.APP_API_KEY

// //pass new generated access token here
const token = accessToken

// Use API_URL from api_urls.js for proper environment switching
const axiosApi = axios.create({
  baseURL: API_URL,
})

// Function to get fresh headers with current session token
// Uses standard Authorization: Bearer header for Node.js backend
const getHeaders = () => {
  const authUser = JSON.parse(localStorage.getItem('authUser'))
  const token = authUser ? authUser.session_token : ""
  return {
    headers: {
      Accept: "application/json",
      "Content-Type": "application/json",
      Authorization: token ? `Bearer ${token}` : "",
      "X-Api-Key": APP_API_KEY
    }
  }
}

// All API calls now use getHeaders() directly for fresh token on each request

// ============================================================================
// OPTIMIZED PAGINATION HELPERS
// ============================================================================
// These functions implement server-side pagination, filtering, and sorting
// for improved performance with large datasets.
// Added as part of FlexFlow ERP Optimization - Phase 0: Foundation
// ============================================================================

/**
 * Generic paginated fetch with server-side filtering, sorting, and search
 *
 * FlexFlow filter syntax:
 * - Simple: `?filter=status=PENDING`
 * - Multiple: `?filter=(status=PENDING) AND (type=CASH)`
 * - Like search: `?filter=(name like %searchterm%)`
 * - OR conditions: `?filter=(name like %x%) OR (email like %x%)`
 *
 * @param {string} endpoint - API endpoint URL (e.g., GET_ORDERS, GET_CUSTOMERS)
 * @param {Object} options - Fetch options
 * @param {number} options.limit - Number of records per page (default: 50)
 * @param {number} options.offset - Starting position for records (default: 0)
 * @param {string|null} options.filter - FlexFlow filter string (e.g., "status=PENDING")
 * @param {string|null} options.search - Search term for text search
 * @param {string[]} options.searchFields - Fields to search in (e.g., ["name", "email", "phone"])
 * @param {string|null} options.orderBy - Field to sort by
 * @param {string} options.orderDir - Sort direction ("ASC" or "DESC")
 * @param {string|null} options.related - Related tables to include (e.g., "orders,payments")
 * @returns {Promise<{data: Array, total: number, hasMore: boolean}>}
 *
 * @example
 * // Basic pagination
 * const result = await fetchPaginated(GET_ORDERS, { limit: 50, offset: 0 })
 *
 * @example
 * // With filter and search
 * const result = await fetchPaginated(GET_CUSTOMERS, {
 *   limit: 50,
 *   offset: 0,
 *   filter: "active=true",
 *   search: "john",
 *   searchFields: ["name", "email", "phone"],
 *   orderBy: "created_at",
 *   orderDir: "DESC"
 * })
 */
export async function fetchPaginated(endpoint, options = {}) {
  const {
    limit = 50,
    offset = 0,
    filter = null,
    search = null,
    searchFields = [],
    orderBy = null,
    orderDir = "DESC",
    related = null,
    fields = null
  } = options

  // Build filter array for FlexFlow
  const filters = []

  // Add custom filter if provided
  // Note: Do NOT wrap in extra parens — the backend silently ignores double-wrapped filters ((..))
  if (filter) {
    filters.push(filter)
  }

  // Add search filter (OR across multiple fields using 'like' operator)
  // Note: FlexFlow 'like' is case-sensitive. See OPTIMIZATION_PLAN.md for future improvement.
  if (search && search.trim() && searchFields.length > 0) {
    const trimmedSearch = search.trim()
    const searchFilters = searchFields
      .map(field => `(${field} like %${trimmedSearch}%)`)
      .join(" OR ")
    filters.push(`(${searchFilters})`)
  }

  // Construct final URL manually to ensure proper encoding
  // FlexFlow expects filter to be URL-encoded properly
  const baseParams = []
  baseParams.push(`limit=${limit}`)
  baseParams.push(`offset=${offset}`)
  baseParams.push(`include_count=true`)

  if (filters.length > 0) {
    // Manually encode the filter to match working examples in codebase
    const filterString = filters.join(" AND ")
    baseParams.push(`filter=${encodeURIComponent(filterString)}`)
  }

  if (orderBy) {
    baseParams.push(`order=${encodeURIComponent(`${orderBy} ${orderDir}`)}`)
  }

  if (related) {
    baseParams.push(`related=${encodeURIComponent(related)}`)
  }

  if (fields) {
    baseParams.push(`fields=${encodeURIComponent(fields)}`)
  }

  // Handle endpoints that might already have query params (like GET_TASKS)
  const separator = endpoint.includes("?") ? "&" : "?"
  const url = `${endpoint}${separator}${baseParams.join("&")}`

  try {
    const response = await fetch(url, {
      headers: getHeaders().headers,
      method: "GET"
    })

    if (!response.ok) {
      throw new Error(`HTTP ${response.status}: ${response.statusText}`)
    }

    const result = await response.json()

    // Handle FlexFlow error responses
    if (result.error) {
      throw new Error(result.error.message || "API request failed")
    }

    // FlexFlow returns count in meta.count when include_count=true
    const total = result.meta?.count ?? result.resource?.length ?? 0
    const data = result.resource || []

    return {
      data,
      total,
      hasMore: (offset + limit) < total
    }
  } catch (error) {
    console.error("fetchPaginated error:", error)
    throw error
  }
}

/**
 * Fetch aggregated statistics for a table (for analytics panels)
 * This performs a separate lightweight fetch just for counts/totals
 *
 * @param {string} endpoint - API endpoint URL
 * @param {Object} filters - Object with filter conditions
 * @returns {Promise<{total: number, filtered: number}>}
 *
 * @example
 * const stats = await fetchStats(GET_ORDERS, { status: "PENDING" })
 */
export async function fetchStats(endpoint, filters = {}) {
  const params = new URLSearchParams()
  params.append("limit", 0) // Don't fetch actual records
  params.append("include_count", "true")

  // Build filter from object
  const filterParts = Object.entries(filters)
    .filter(([_, value]) => value !== null && value !== undefined && value !== "")
    .map(([key, value]) => `(${key}=${value})`)

  if (filterParts.length > 0) {
    params.append("filter", filterParts.join(" AND "))
  }

  const separator = endpoint.includes("?") ? "&" : "?"
  const url = `${endpoint}${separator}${params.toString()}`

  try {
    const response = await fetch(url, {
      headers: getHeaders().headers,
      method: "GET"
    })

    const result = await response.json()

    return {
      total: result.meta?.count ?? 0
    }
  } catch (error) {
    console.error("fetchStats error:", error)
    return { total: 0 }
  }
}

// ============================================================================
// PAGINATED FETCH FUNCTIONS FOR SPECIFIC ENTITIES
// ============================================================================
// These wrap fetchPaginated with entity-specific defaults and search fields

/**
 * Paginated Orders fetch with server-side filtering/sorting
 * Search fields: customer_name, notes (searchable text fields on orders)
 *
 * Supports:
 * - statusFilter: Filter by order status (e.g., "PENDING-STOCK", "SHIPPED")
 * - dateRangeFilter: FlexFlow filter string for date range
 *
 * @param {Object} options - Fetch options
 * @param {string} options.statusFilter - Status to filter by ("ALL" for no filter)
 * @param {string} options.dateRangeFilter - Date range filter string
 */
export async function getOrdersPaginated(options = {}) {
  const { statusFilter, dateRangeFilter, ...restOptions } = options

  // Build combined filter
  const filters = []

  // Add status filter
  if (statusFilter && statusFilter !== "ALL") {
    filters.push(`status=${statusFilter}`)
  }

  // Add date range filter (already formatted as FlexFlow filter string)
  if (dateRangeFilter) {
    filters.push(dateRangeFilter)
  }

  // Combine filters with AND
  const filter = filters.length > 0 ? filters.join(") AND (") : null
  const finalFilter = filter ? `(${filter})` : null

  return fetchPaginated(GET_ORDERS, {
    searchFields: ["customer_name", "notes", "name", "label"],
    orderBy: "expected_delivery_date",
    orderDir: "DESC",
    filter: finalFilter,
    ...restOptions
  })
}

/**
 * Paginated Customers fetch with server-side filtering/sorting
 * Search fields: name, label, vat, phone, email, address
 *
 * Supports:
 * - categoryFilter: FlexFlow filter string for category (e.g., "category=Retail")
 *
 * @param {Object} options - Fetch options
 * @param {string} options.categoryFilter - Category filter string (null for no filter)
 */
export async function getCustomersPaginated(options = {}) {
  const { categoryFilter, ...restOptions } = options

  return fetchPaginated(GET_CUSTOMERS, {
    searchFields: ["name", "label", "vat", "phone", "email", "address"],
    orderBy: "name",
    orderDir: "ASC",
    filter: categoryFilter || null,
    ...restOptions
  })
}

/**
 * Paginated Products fetch with server-side filtering/sorting
 * Search fields: name, sku, category
 *
 * Supports:
 * - usageType: Filter by product usage type (e.g., "MATERIAL", "COMPLETE-PRODUCT")
 * - categoryFilter: FlexFlow filter string for category (e.g., "category=Electronics")
 *
 * @param {Object} options - Fetch options
 * @param {string} options.usageType - Usage type to filter by
 * @param {string} options.categoryFilter - Category filter string (null for no filter)
 */
export async function getProductsPaginated(options = {}) {
  const { usageType, categoryFilter, positionFilter, golaFilter, openingFilter, ...restOptions } = options

  // Build individual filter conditions (bare — no outer parens)
  const filters = []
  if (usageType) filters.push(`usageType=${usageType}`)
  if (positionFilter) filters.push(`cabinet_position=${positionFilter}`)
  if (golaFilter) filters.push(`category like %,"GOLA"%`)
  if (openingFilter) filters.push(`cabinet_opening=${openingFilter}`)
  if (categoryFilter) filters.push(categoryFilter)

  // Wrap each condition and join with AND
  const finalFilter = filters.length > 0
    ? filters.map(f => `(${f})`).join(" AND ")
    : null

  return fetchPaginated(GET_PRODUCTS, {
    searchFields: ["name", "sku", "category", "description"],
    orderBy: "name",
    orderDir: "ASC",
    filter: finalFilter,
    ...restOptions
  })
}

/**
 * Paginated Invoices fetch with server-side filtering/sorting
 * Search fields: number, providername, refcode, providervat
 *
 * @param {Object} options - Fetch options
 * @param {string} options.providerFilter - Filter by provider name
 * @param {string} options.dateRangeFilter - Date range filter string
 */
export async function getInvoicesPaginated(options = {}) {
  const { providerFilter, dateRangeFilter, ...restOptions } = options

  // Build filters
  const filters = []
  if (providerFilter && providerFilter !== "ALL") {
    filters.push(`providername=${providerFilter}`)
  }
  if (dateRangeFilter) {
    filters.push(dateRangeFilter)
  }

  const filter = filters.length > 0 ? filters.join(") AND (") : null

  return fetchPaginated(INVOICES, {
    // Note: providervat is not a searchable field in FlexFlow
    searchFields: ["number", "providername", "refcode"],
    orderBy: "importDate",
    orderDir: "DESC",
    filter: filter ? `(${filter})` : null,
    ...restOptions
  })
}

/**
 * Fetch invoice analytics/statistics for the analytics panel
 * This is a lightweight query that doesn't load individual records
 *
 * @returns {Promise<Object>} Analytics data including totals and counts
 */
export async function fetchInvoiceAnalytics() {
  try {
    // Fetch all invoices with minimal fields for analytics calculation
    // We need the totals field which is JSON, so we need to process it client-side
    // But we limit the fields fetched to minimize data transfer
    const response = await fetch(`${INVOICES}?fields=id,totals,providername,importDate`, {
      headers: getHeaders().headers,
      method: "GET"
    })

    if (!response.ok) {
      throw new Error(`HTTP ${response.status}: ${response.statusText}`)
    }

    const result = await response.json()
    const invoices = result.resource || []

    // Process analytics
    let totalAmount = 0
    let totalVat = 0
    let totalSubtotal = 0
    let totalItems = 0
    const providerCounts = {}
    const monthCounts = {}

    invoices.forEach(invoice => {
      // Parse totals JSON
      let parsedTotals = {}
      try {
        parsedTotals = JSON.parse(invoice.totals || "{}")
      } catch (e) {
        parsedTotals = {}
      }

      totalAmount += parsedTotals.totalOrder || 0
      totalVat += parsedTotals.totalVat || 0
      totalSubtotal += parsedTotals.totalPrdAmmount || 0
      totalItems += parsedTotals.totalQuantity || 0

      // Count by provider
      const providerName = invoice.providername || "Unknown"
      providerCounts[providerName] = (providerCounts[providerName] || 0) + 1

      // Count by month
      if (invoice.importDate) {
        const monthKey = invoice.importDate.substring(0, 7) // YYYY-MM
        if (!monthCounts[monthKey]) {
          monthCounts[monthKey] = { count: 0, total: 0 }
        }
        monthCounts[monthKey].count += 1
        monthCounts[monthKey].total += parsedTotals.totalOrder || 0
      }
    })

    // Format top providers
    const topProviders = Object.entries(providerCounts)
      .map(([name, count]) => ({ name, count }))
      .sort((a, b) => b.count - a.count)
      .slice(0, 5)

    // Format monthly data
    const byMonth = Object.entries(monthCounts)
      .map(([month, data]) => ({ month, ...data }))
      .sort((a, b) => b.month.localeCompare(a.month))

    return {
      total: invoices.length,
      totalAmount,
      totalVat,
      totalSubtotal,
      totalItems,
      providersCount: Object.keys(providerCounts).length,
      topProviders,
      byMonth
    }
  } catch (error) {
    console.error("fetchInvoiceAnalytics error:", error)
    return {
      total: 0,
      totalAmount: 0,
      totalVat: 0,
      totalSubtotal: 0,
      totalItems: 0,
      providersCount: 0,
      topProviders: [],
      byMonth: []
    }
  }
}

/**
 * Paginated Payments fetch with server-side filtering/sorting
 * Search fields: entity_name, series, bankname
 *
 * @param {Object} options - Fetch options
 * @param {string} options.bound - Filter by bound type ("Inbound", "Outbound", or "ALL")
 * @param {string} options.type - Filter by payment type ("CASH", "CARD", "BANK", "CHECK", or "ALL")
 */
export async function getPaymentsPaginated(options = {}) {
  const { bound, type, ...restOptions } = options

  // Build filters
  const filters = []
  if (bound && bound !== "ALL" && bound !== "all") {
    filters.push(`bound=${bound}`)
  }
  if (type && type !== "ALL" && type !== "all") {
    filters.push(`type=${type}`)
  }

  const filter = filters.length > 0 ? filters.join(") AND (") : null

  return fetchPaginated(PAYMENTS, {
    searchFields: ["entity_name", "series", "bankname"],
    orderBy: "date",
    orderDir: "DESC",
    filter: filter ? `(${filter})` : null,
    ...restOptions
  })
}

/**
 * Fetch payment analytics/statistics for the analytics panel
 * This fetches summary data for charts and stats without loading all records
 *
 * @returns {Promise<Object>} Analytics data including totals, counts by type/bound
 */
export async function fetchPaymentAnalytics() {
  try {
    // Fetch all payments with minimal fields for analytics
    const response = await fetch(`${PAYMENTS}?fields=id,ammount,bound,type,date`, {
      headers: getHeaders().headers,
      method: "GET"
    })

    if (!response.ok) {
      throw new Error(`HTTP ${response.status}: ${response.statusText}`)
    }

    const result = await response.json()
    const payments = result.resource || []

    // Calculate statistics
    let inboundTotal = 0
    let outboundTotal = 0
    let inboundCount = 0
    let outboundCount = 0
    const methodCounts = {}
    const methodAmounts = {}
    const paymentTypes = new Set()

    payments.forEach(payment => {
      const amount = Number(payment.ammount) || 0
      const type = payment.type || "Unknown"

      if (payment.bound === "Inbound") {
        inboundTotal += amount
        inboundCount++
      } else if (payment.bound === "Outbound") {
        outboundTotal += amount
        outboundCount++
      }

      // Count by method
      methodCounts[type] = (methodCounts[type] || 0) + 1
      methodAmounts[type] = (methodAmounts[type] || 0) + amount
      paymentTypes.add(type)
    })

    // Format method chart data
    const colors = { CASH: "#10b981", CARD: "#3b82f6", BANK: "#8b5cf6", CHECK: "#f59e0b" }
    const methodChartData = Object.entries(methodCounts)
      .map(([name, count]) => ({
        name,
        count,
        amount: methodAmounts[name],
        color: colors[name] || "#6b7280"
      }))
      .sort((a, b) => b.count - a.count)

    return {
      total: payments.length,
      inboundTotal,
      outboundTotal,
      inboundCount,
      outboundCount,
      net: inboundTotal - outboundTotal,
      methodChartData,
      paymentTypes: Array.from(paymentTypes).sort()
    }
  } catch (error) {
    console.error("fetchPaymentAnalytics error:", error)
    return {
      total: 0,
      inboundTotal: 0,
      outboundTotal: 0,
      inboundCount: 0,
      outboundCount: 0,
      net: 0,
      methodChartData: [],
      paymentTypes: []
    }
  }
}

/**
 * Paginated Providers fetch with server-side filtering/sorting
 * Search fields: name, label, vat, phone, email, address
 */
export async function getProvidersPaginated(options = {}) {
  return fetchPaginated(GET_PROVIDERS, {
    // Note: label, address are not searchable in FlexFlow
    searchFields: ["name", "vat", "phone", "email"],
    orderBy: "name",
    orderDir: "ASC",
    ...options
  })
}

/**
 * Fetch provider analytics/statistics for the analytics panel
 *
 * @returns {Promise<Object>} Analytics data including contact info completeness and city distribution
 */
export async function fetchProviderAnalytics() {
  try {
    // Fetch all providers with minimal fields for analytics
    const response = await fetch(`${GET_PROVIDERS}?fields=id,name,phone,email,vat,address`, {
      headers: getHeaders().headers,
      method: "GET"
    })

    if (!response.ok) {
      throw new Error(`HTTP ${response.status}: ${response.statusText}`)
    }

    const result = await response.json()
    const providers = result.resource || []

    // Calculate statistics
    let withPhone = 0
    let withEmail = 0
    let withVat = 0
    let withAddress = 0
    const cityCounts = {}

    providers.forEach(provider => {
      if (provider.phone) withPhone++
      if (provider.email) withEmail++
      if (provider.vat) withVat++
      if (provider.address) withAddress++

      // Extract city from address
      const address = provider.address || ""
      const cityMatch = address.match(/,\s*([^,]+)$/) || address.match(/([^,]+)/)
      const city = cityMatch ? cityMatch[1].trim() : null
      if (city && city.length > 1) {
        cityCounts[city] = (cityCounts[city] || 0) + 1
      }
    })

    // Format city chart data
    const colors = ["#8b5cf6", "#3b82f6", "#10b981", "#f59e0b", "#ef4444", "#6366f1"]
    const cityChartData = Object.entries(cityCounts)
      .map(([name, count], i) => ({ name, count, color: colors[i % colors.length] }))
      .sort((a, b) => b.count - a.count)
      .slice(0, 6)

    return {
      total: providers.length,
      withPhone,
      withEmail,
      withVat,
      withAddress,
      cityChartData
    }
  } catch (error) {
    console.error("fetchProviderAnalytics error:", error)
    return {
      total: 0,
      withPhone: 0,
      withEmail: 0,
      withVat: 0,
      withAddress: 0,
      cityChartData: []
    }
  }
}

/**
 * Paginated Loans fetch with server-side filtering/sorting
 * Search fields: customer_name, product_name
 */
export async function getLoansPaginated(options = {}) {
  return fetchPaginated(CREATE_LOAN, {
    searchFields: ["customer_name", "product_name"],
    orderBy: "created_at",
    orderDir: "DESC",
    ...options
  })
}

/**
 * Fetch loan data with only required fields for analytics
 * This is optimized for the loans dashboard which computes charts from all data
 *
 * @returns {Promise<Array>} Array of loan records with essential fields
 */
export async function fetchLoanAnalytics() {
  try {
    // Fetch only required fields for analytics to minimize data transfer
    const fields = "id,bound,quantity,prdname,custname,customer_id,prd_id,order_id"
    const response = await fetch(`${CREATE_LOAN}?fields=${fields}`, {
      headers: getHeaders().headers,
      method: "GET"
    })

    if (!response.ok) {
      throw new Error(`HTTP ${response.status}: ${response.statusText}`)
    }

    const result = await response.json()
    return result.resource || []
  } catch (error) {
    console.error("fetchLoanAnalytics error:", error)
    return []
  }
}

/**
 * Paginated Tasks fetch with server-side filtering/sorting
 * Note: GET_TASKS already has a filter for status!=FINISHED
 * Search fields: name, description
 */
export async function getTasksPaginated(options = {}) {
  const { typeFilter, statusFilter, ...restOptions } = options

  // Build additional filters (GET_TASKS already filters out FINISHED)
  const filters = []
  if (typeFilter && typeFilter !== "ALL") {
    filters.push(`type=${typeFilter}`)
  }
  if (statusFilter && statusFilter !== "ALL" && statusFilter !== "FINISHED") {
    filters.push(`status=${statusFilter}`)
  }

  const filter = filters.length > 0 ? filters.join(") AND (") : null

  return fetchPaginated(GET_TASKS, {
    searchFields: ["name", "description"],
    orderBy: "created_at",
    orderDir: "DESC",
    filter: filter ? `(${filter})` : null,
    ...restOptions
  })
}

/**
 * Fetch tasks with only required fields for grouping by order
 * This is optimized for the Tasks dashboard which groups tasks by orderref
 *
 * @returns {Promise<Array>} Array of task records with essential fields for grouping
 */
export async function fetchTasksForGrouping() {
  try {
    // Fetch only required fields for grouping to minimize data transfer
    // Note: GET_TASKS already filters out FINISHED tasks
    const fields = "id,orderref,name,type,status,photos,seq,quantity,customer_name,expectedDate,order"
    const response = await fetch(`${GET_TASKS}?fields=${fields}&order=orderref DESC`, {
      headers: getHeaders().headers,
      method: "GET"
    })

    if (!response.ok) {
      throw new Error(`HTTP ${response.status}: ${response.statusText}`)
    }

    const result = await response.json()
    return result.resource || []
  } catch (error) {
    console.error("fetchTasksForGrouping error:", error)
    return []
  }
}

/**
 * Paginated Salespersons fetch with server-side filtering/sorting
 * Search fields: name, email, phone
 */
export async function getSalespersonsPaginated(options = {}) {
  return fetchPaginated(SALESPERSONS, {
    searchFields: ["name", "surname", "email", "phone"],
    orderBy: "name",
    orderDir: "ASC",
    ...options
  })
}

/**
 * Paginated Shipments fetch with server-side filtering/sorting
 * Search fields: customer_name, driver_name
 */
export async function getShipmentsPaginated(options = {}) {
  const { statusFilter, driverId, ...restOptions } = options

  // Build filters
  const filters = []
  if (statusFilter && statusFilter !== "ALL") {
    filters.push(`status=${statusFilter}`)
  }
  if (driverId) {
    filters.push(`driver_id=${driverId}`)
  }

  const filter = filters.length > 0 ? filters.join(") AND (") : null

  return fetchPaginated(SHIPPING, {
    searchFields: ["customer_name", "driver_name", "notes"],
    orderBy: "created_at",
    orderDir: "DESC",
    filter: filter ? `(${filter})` : null,
    ...restOptions
  })
}

/**
 * Paginated Materials fetch with server-side filtering/sorting
 * Materials are products with usageType = 'MATERIAL'
 */
export async function getMaterialsPaginated(options = {}) {
  return fetchPaginated(GET_PRODUCTS, {
    searchFields: ["name", "sku", "category"],
    orderBy: "name",
    orderDir: "ASC",
    filter: "usageType=MATERIAL",
    ...options
  })
}

// ============================================================================
// DASHBOARD OPTIMIZATION HELPERS
// ============================================================================
// These functions provide optimized data fetching for dashboard pages.
// Dashboards require full data for client-side aggregations (FlexFlow
// doesn't support GROUP BY), but we fetch only the fields needed.
// ============================================================================

/**
 * Fetch customer data optimized for the main dashboard
 * Returns customers with their orders and payments, but only essential fields
 *
 * @returns {Promise<Array>} Array of customers with orders and payments
 */
export async function fetchDashboardData() {
  try {
    // Fetch customers, orders, and payments separately (no DreamFactory-style related queries)
    const [customersRes, ordersRes, paymentsRes] = await Promise.all([
      fetch(`${GET_CUSTOMERS}?fields=id,name`, { headers: getHeaders().headers, method: "GET" }),
      fetch(`${GET_ORDERS}?fields=id,customer_id,status,totals`, { headers: getHeaders().headers, method: "GET" }),
      fetch(`${PAYMENTS}?fields=id,entity_id,ammount,bound`, { headers: getHeaders().headers, method: "GET" })
    ])

    if (!customersRes.ok || !ordersRes.ok || !paymentsRes.ok) {
      throw new Error("Failed to fetch dashboard data")
    }

    const [customersData, ordersData, paymentsData] = await Promise.all([
      customersRes.json(),
      ordersRes.json(),
      paymentsRes.json()
    ])

    const customers = customersData.resource || []
    const orders = ordersData.resource || []
    const payments = paymentsData.resource || []

    // Group orders and payments by customer_id
    const ordersByCustomer = {}
    const paymentsByCustomer = {}

    orders.forEach(order => {
      const cid = order.customer_id
      if (!ordersByCustomer[cid]) ordersByCustomer[cid] = []
      ordersByCustomer[cid].push(order)
    })

    // Customer payments have bound="Inbound" and entity_id is the customer id
    payments.filter(p => p.bound === "Inbound").forEach(payment => {
      const cid = payment.entity_id
      if (cid) {
        if (!paymentsByCustomer[cid]) paymentsByCustomer[cid] = []
        paymentsByCustomer[cid].push(payment)
      }
    })

    // Process customer data to compute totals
    const customersWithShippedOrders = customers.filter(customer =>
      ordersByCustomer[customer.id]?.some(order => order.status === "SHIPPED")
    )

    const processedData = customersWithShippedOrders.map(customer => {
      const customerOrders = ordersByCustomer[customer.id] || []
      const customerPayments = paymentsByCustomer[customer.id] || []

      const shippedOrders = customerOrders.filter(order => order.status === "SHIPPED")
      const { totalOrders, orderCount } = shippedOrders.reduce(
        (acc, order) => {
          try {
            const parsedTotals = JSON.parse(order.totals || "{}")
            return {
              totalOrders: acc.totalOrders + (Number(parsedTotals.totalOrder) || 0),
              orderCount: acc.orderCount + 1
            }
          } catch {
            return acc
          }
        },
        { totalOrders: 0, orderCount: 0 }
      )

      const totalPayments = customerPayments.reduce(
        (acc, payment) => acc + (Number(payment.ammount) || 0),
        0
      )

      return {
        id: customer.id,
        name: customer.name,
        totalOrders,
        totalPayments,
        orderCount,
        totalCustomer: totalOrders - totalPayments
      }
    })

    // Sort by total orders descending
    processedData.sort((a, b) => b.totalOrders - a.totalOrders)

    // Calculate summary stats
    const totalSales = processedData.reduce((acc, c) => acc + c.totalOrders, 0)
    const totalIncome = processedData.reduce((acc, c) => acc + c.totalPayments, 0)

    return {
      customerTotals: processedData,
      totalSales,
      totalIncome,
      totalOrderCount: processedData.reduce((acc, c) => acc + c.orderCount, 0),
      totalCustomerCount: processedData.length
    }
  } catch (error) {
    console.error("fetchDashboardData error:", error)
    return {
      customerTotals: [],
      totalSales: 0,
      totalIncome: 0,
      totalOrderCount: 0,
      totalCustomerCount: 0
    }
  }
}

/**
 * Fetch data optimized for the Top Products dashboard
 * Extracts product sales from shipped orders
 *
 * @returns {Promise<Object>} Object with product sales data
 */
export async function fetchTopProductsData() {
  try {
    // Fetch only shipped/invoiced orders with products field
    const response = await fetch(
      `${GET_ORDERS}?filter=(status=SHIPPED) OR (status=INVOICED)&fields=id,status,products`,
      {
        headers: getHeaders().headers,
        method: "GET"
      }
    )

    if (!response.ok) {
      throw new Error(`HTTP ${response.status}: ${response.statusText}`)
    }

    const result = await response.json()
    const orders = result.resource || []

    // Extract product sales from orders
    const productSales = {}
    orders.forEach(order => {
      try {
        const products = JSON.parse(order.products || "[]")
        products.forEach(product => {
          const { name, quantity } = product
          if (name) {
            productSales[name] = (productSales[name] || 0) + (Number(quantity) || 0)
          }
        })
      } catch {
        // Skip invalid products JSON
      }
    })

    // Sort products by quantity sold
    const sortedProducts = Object.entries(productSales)
      .map(([name, quantity], index) => ({
        id: index + 1,
        rank: index + 1,
        name,
        quantity: Number(quantity)
      }))
      .sort((a, b) => b.quantity - a.quantity)

    // Re-assign ranks after sorting
    sortedProducts.forEach((p, i) => {
      p.rank = i + 1
      p.id = i + 1
    })

    return {
      allProducts: sortedProducts,
      topProducts: sortedProducts.slice(0, 10),
      totalQuantity: sortedProducts.reduce((sum, p) => sum + p.quantity, 0)
    }
  } catch (error) {
    console.error("fetchTopProductsData error:", error)
    return {
      allProducts: [],
      topProducts: [],
      totalQuantity: 0
    }
  }
}

/**
 * Fetch data optimized for the Top Materials dashboard
 * Extracts material purchases from provider orders
 *
 * @returns {Promise<Object>} Object with material purchase data
 */
export async function fetchTopMaterialsData() {
  try {
    // Fetch providers with their orders (content field contains materials)
    const response = await fetch(
      `${GET_PROVIDERS}?related=orders_by_provider_id&fields=id,name&orders_by_provider_id.fields=id,content`,
      {
        headers: getHeaders().headers,
        method: "GET"
      }
    )

    if (!response.ok) {
      throw new Error(`HTTP ${response.status}: ${response.statusText}`)
    }

    const result = await response.json()
    const providers = result.resource || []

    // Extract material quantities from provider orders
    const materialQuantities = {}
    providers.forEach(provider => {
      const orders = provider.orders_by_provider_id || []
      orders.forEach(order => {
        try {
          const products = JSON.parse(order.content || "[]")
          products.forEach(product => {
            const { name, quantity } = product
            if (name) {
              materialQuantities[name] = (materialQuantities[name] || 0) + (Number(quantity) || 0)
            }
          })
        } catch {
          // Skip invalid content JSON
        }
      })
    })

    // Sort materials by quantity purchased
    const sortedMaterials = Object.entries(materialQuantities)
      .map(([name, quantity], index) => ({
        id: index + 1,
        rank: index + 1,
        name,
        quantity: Number(quantity)
      }))
      .sort((a, b) => b.quantity - a.quantity)

    // Re-assign ranks after sorting
    sortedMaterials.forEach((m, i) => {
      m.rank = i + 1
      m.id = i + 1
    })

    return {
      allMaterials: sortedMaterials,
      topMaterials: sortedMaterials.slice(0, 10),
      totalQuantity: sortedMaterials.reduce((sum, m) => sum + m.quantity, 0)
    }
  } catch (error) {
    console.error("fetchTopMaterialsData error:", error)
    return {
      allMaterials: [],
      topMaterials: [],
      totalQuantity: 0
    }
  }
}

/**
 * Fetch data optimized for the Providers Dashboard
 * Returns provider totals from orders and payments
 *
 * @returns {Promise<Object>} Object with provider totals data
 */
export async function fetchProviderDashboardData() {
  try {
    // Fetch providers, invoices (provider orders), and payments separately
    const [providersRes, invoicesRes, paymentsRes] = await Promise.all([
      fetch(`${GET_PROVIDERS}?fields=id,name`, { headers: getHeaders().headers, method: "GET" }),
      fetch(`${INVOICES}?fields=id,providerid,totals`, { headers: getHeaders().headers, method: "GET" }),
      fetch(`${PAYMENTS}?fields=id,entity_id,ammount,bound`, { headers: getHeaders().headers, method: "GET" })
    ])

    if (!providersRes.ok || !invoicesRes.ok || !paymentsRes.ok) {
      throw new Error("Failed to fetch provider dashboard data")
    }

    const [providersData, invoicesData, paymentsData] = await Promise.all([
      providersRes.json(),
      invoicesRes.json(),
      paymentsRes.json()
    ])

    const providers = providersData.resource || []
    const invoices = invoicesData.resource || []
    const payments = paymentsData.resource || []

    // Group invoices and payments by provider id
    const invoicesByProvider = {}
    const paymentsByProvider = {}

    invoices.forEach(invoice => {
      const pid = invoice.providerid
      if (pid) {
        if (!invoicesByProvider[pid]) invoicesByProvider[pid] = []
        invoicesByProvider[pid].push(invoice)
      }
    })

    // Provider payments have bound="Outbound" and entity_id is the provider id
    payments.filter(p => p.bound === "Outbound").forEach(payment => {
      const pid = payment.entity_id
      if (pid) {
        if (!paymentsByProvider[pid]) paymentsByProvider[pid] = []
        paymentsByProvider[pid].push(payment)
      }
    })

    // Process providers with invoices
    const providersWithInvoices = providers.filter(
      provider => invoicesByProvider[provider.id] && invoicesByProvider[provider.id].length > 0
    )

    const totals = providersWithInvoices.map(provider => {
      const providerInvoices = invoicesByProvider[provider.id] || []
      const providerPayments = paymentsByProvider[provider.id] || []

      const { totalOrders, orderCount } = providerInvoices.reduce(
        (acc, invoice) => {
          try {
            const parsedTotals = JSON.parse(invoice.totals || "{}")
            return {
              totalOrders: acc.totalOrders + (Number(parsedTotals.totalOrder) || 0),
              orderCount: acc.orderCount + 1
            }
          } catch {
            return acc
          }
        },
        { totalOrders: 0, orderCount: 0 }
      )

      const totalPayments = providerPayments.reduce(
        (acc, payment) => acc + (Number(payment.ammount) || 0),
        0
      )

      return {
        id: provider.id,
        name: provider.name,
        totalOrders,
        totalPayments,
        orderCount,
        balance: totalOrders - totalPayments
      }
    })

    // Sort by total orders descending
    totals.sort((a, b) => b.totalOrders - a.totalOrders)

    // Calculate summary stats
    const totalOrdersValue = totals.reduce((sum, p) => sum + p.totalOrders, 0)
    const totalPaymentsValue = totals.reduce((sum, p) => sum + p.totalPayments, 0)
    const totalBalance = totals.reduce((sum, p) => sum + p.balance, 0)
    const totalOrderCount = totals.reduce((sum, p) => sum + p.orderCount, 0)

    return {
      providerTotals: totals,
      summaryStats: {
        totalProviders: totals.length,
        totalOrdersValue,
        totalPaymentsValue,
        totalBalance,
        totalOrderCount
      }
    }
  } catch (error) {
    console.error("fetchProviderDashboardData error:", error)
    return {
      providerTotals: [],
      summaryStats: {
        totalProviders: 0,
        totalOrdersValue: 0,
        totalPaymentsValue: 0,
        totalBalance: 0,
        totalOrderCount: 0
      }
    }
  }
}

/**
 * Fetch data optimized for the Analytics Dashboard
 * Fetches orders, invoices, and payments with only required fields
 *
 * @returns {Promise<Object>} Object with analytics data
 */
export async function fetchAnalyticsDashboardData() {
  try {
    // Fetch all data in parallel with only required fields
    const [ordersRes, invoicesRes, paymentsRes] = await Promise.all([
      fetch(`${GET_ORDERS}?fields=id,status,name,totals,products,created,updated,expected_delivery_date`, {
        headers: getHeaders().headers,
        method: "GET"
      }),
      fetch(`${INVOICES}?fields=id,totals,refcode,importDate`, {
        headers: getHeaders().headers,
        method: "GET"
      }),
      fetch(`${PAYMENTS}?fields=id,ammount,bound,type,date`, {
        headers: getHeaders().headers,
        method: "GET"
      })
    ])

    // Check all responses
    if (!ordersRes.ok || !invoicesRes.ok || !paymentsRes.ok) {
      throw new Error("Failed to fetch analytics data")
    }

    const [ordersData, invoicesData, paymentsData] = await Promise.all([
      ordersRes.json(),
      invoicesRes.json(),
      paymentsRes.json()
    ])

    return {
      orders: ordersData.resource || [],
      invoices: invoicesData.resource || [],
      payments: paymentsData.resource || []
    }
  } catch (error) {
    console.error("fetchAnalyticsDashboardData error:", error)
    return {
      orders: [],
      invoices: [],
      payments: []
    }
  }
}

// ============================================================================
// END OF OPTIMIZATION HELPERS
// ============================================================================

// ============================================================================
// QUOTES CRUD (CPQ Compositions)
// ============================================================================

export async function getQuotesByUser(userId) {
  const url = `${QUOTES}?filter=${encodeURIComponent(`user_id=${userId}`)}&order=${encodeURIComponent("updated_at DESC")}`
  const response = await fetch(url, { headers: getHeaders().headers, method: "GET" })
  const result = await response.json()
  return result.resource || []
}

// Stringify JSONB fields for PostgreSQL via Knex
function prepareQuoteData(data) {
  const prepared = { ...data }
  if (prepared.config && typeof prepared.config === "object") {
    prepared.config = JSON.stringify(prepared.config)
  }
  if (prepared.cart && typeof prepared.cart === "object") {
    prepared.cart = JSON.stringify(prepared.cart)
  }
  return prepared
}

export async function createQuote(data) {
  const response = await fetch(QUOTES, {
    body: JSON.stringify({ resource: [prepareQuoteData(data)] }),
    headers: getHeaders().headers,
    method: "POST",
  })
  const result = await response.json()
  return result.resource ? result.resource[0] : result
}

export async function updateQuote(data) {
  const response = await fetch(QUOTES, {
    body: JSON.stringify({ resource: [prepareQuoteData(data)] }),
    headers: getHeaders().headers,
    method: "PATCH",
  })
  const result = await response.json()
  return result.resource ? result.resource[0] : result
}

export async function getQuotesByStatus(status) {
  const filter = status ? `status=${status}` : ""
  const url = `${QUOTES}?filter=${encodeURIComponent(filter)}&order=${encodeURIComponent("updated_at DESC")}&limit=200`
  const response = await fetch(url, { headers: getHeaders().headers, method: "GET" })
  const result = await response.json()
  return result.resource || []
}

export async function getAllQuotes() {
  const url = `${QUOTES}?order=${encodeURIComponent("updated_at DESC")}&limit=200`
  const response = await fetch(url, { headers: getHeaders().headers, method: "GET" })
  const result = await response.json()
  return result.resource || []
}

export async function deleteQuoteById(id) {
  const response = await fetch(`${QUOTES}/${id}`, {
    headers: getHeaders().headers,
    method: "DELETE",
  })
  return response.json()
}

export async function deleteProduct(data, config = {}) {
  const options = {
    body: JSON.stringify(data
    ),
    headers: getHeaders().headers,
    method: "DELETE"
  }
  return await fetch(GET_PRODUCTS, options)
    .then(response => response.json())
    .then(data => data);
}


export async function getSalesPersonsCustomers(id) {
  if (id) {
    return await fetch(GET_CUSTOMERS + "?filter=salespersons=" + id, getHeaders())
      .then(response => response.json())
      .then(data => data.resource);
  }
  if (!id) {
    return await fetch(GET_CUSTOMERS, getHeaders())
      .then(response => response.json())
      .then(data => data.resource);
  }
}


export async function postSalesperson(data, config = {}) {
  const options = {
    body: JSON.stringify(data
    ),
    headers: getHeaders().headers,
    method: "POST"
  }
  return await fetch(SALESPERSONS, options)
    .then(response => response.json())
    .then(data => data);
}

export async function getSalesPersons(id) {
  if (id) {
    return await fetch(SALESPERSONS + "?filter=id=" + id, getHeaders())
      .then(response => response.json())
      .then(data => data.resource);
  }
  if (!id) {
    return await fetch(SALESPERSONS, getHeaders())
      .then(response => response.json())
      .then(data => data.resource);
  }
}

export async function patchSalesPerson(data, config = {}) {
  const options = {
    body: JSON.stringify(data
    ),
    headers: getHeaders().headers,
    method: "PATCH"
  }
  return await fetch(SALESPERSONS, options)
    .then(response => response.json())
    .then(data => data);
}

// FlexFlow User & Role Management

// Fetch all roles from FlexFlow
export async function getDFRoles() {
  return await fetch(SYSTEM_ROLES, getHeaders())
    .then(response => response.json())
    .then(data => {
      if (data.error) {
        throw new Error(data.error.message || "Failed to fetch roles")
      }
      return data.resource || []
    })
}

// Fetch all users with their role assignments
export async function getDFUsers() {
  const url = `${SYSTEM_USERS}?related=user_to_app_to_role_by_user_id`
  return await fetch(url, getHeaders())
    .then(response => response.json())
    .then(data => {
      if (data.error) {
        throw new Error(data.error.message || "Failed to fetch users")
      }
      return data.resource || []
    })
}

// Fetch users filtered by role ID
export async function getDFUsersByRole(roleId) {
  // First get all users with their role assignments
  const url = `${SYSTEM_USERS}?related=user_to_app_to_role_by_user_id`
  return await fetch(url, getHeaders())
    .then(response => response.json())
    .then(data => {
      if (data.error) {
        throw new Error(data.error.message || "Failed to fetch users")
      }
      const users = data.resource || []
      // Filter users who have the specified role
      return users.filter(user => {
        const roles = user.user_to_app_to_role_by_user_id || []
        return roles.some(r => r.role_id === parseInt(roleId))
      })
    })
}

// Get a single user by ID with role assignments
export async function getDFUserById(userId) {
  const url = `${SYSTEM_USERS}/${userId}?related=user_to_app_to_role_by_user_id`
  return await fetch(url, getHeaders())
    .then(response => response.json())
    .then(data => {
      if (data.error) {
        throw new Error(data.error.message || "Failed to fetch user")
      }
      return data
    })
}

// Create a new user with role assignment
export async function createDFUser(userData, roleId = null, appId = DEFAULT_APP_ID) {
  // userData should include: email, password, name, first_name, last_name, phone
  const userDataWithRole = { ...userData }

  // If roleId is provided, include the role assignment (app_id is required by FlexFlow)
  if (roleId) {
    const roleAssignment = {
      role_id: parseInt(roleId),
      app_id: parseInt(appId || DEFAULT_APP_ID)
    }
    userDataWithRole.user_to_app_to_role_by_user_id = [roleAssignment]
  }

  const url = roleId
    ? `${SYSTEM_USERS}?related=user_to_app_to_role_by_user_id`
    : SYSTEM_USERS

  const options = {
    body: JSON.stringify({ resource: [userDataWithRole] }),
    headers: getHeaders().headers,
    method: "POST"
  }

  return await fetch(url, options)
    .then(response => response.json())
    .then(data => {
      if (data.error) {
        throw new Error(data.error.message || "Failed to create user")
      }
      const result = data.resource && data.resource[0]
      if (typeof result === 'number') {
        return { id: result }
      }
      return result || data
    })
}

// Update an existing user
export async function updateDFUser(userId, userData, roleId = null, appId = DEFAULT_APP_ID) {
  // userData can include: email, password, name, first_name, last_name, is_active, phone
  const userDataWithRole = { id: parseInt(userId), ...userData }

  // If roleId is provided, update the role assignment (app_id is required by FlexFlow)
  if (roleId) {
    const roleAssignment = {
      role_id: parseInt(roleId),
      app_id: parseInt(appId || DEFAULT_APP_ID)
    }
    userDataWithRole.user_to_app_to_role_by_user_id = [roleAssignment]
  }

  const url = roleId
    ? `${SYSTEM_USERS}?related=user_to_app_to_role_by_user_id`
    : SYSTEM_USERS

  const options = {
    body: JSON.stringify({ resource: [userDataWithRole] }),
    headers: getHeaders().headers,
    method: "PATCH"
  }

  return await fetch(url, options)
    .then(response => response.json())
    .then(data => {
      if (data.error) {
        throw new Error(data.error.message || "Failed to update user")
      }
      return data.resource && data.resource[0] ? data.resource[0] : data
    })
}

// Delete a user
export async function deleteDFUser(userId) {
  const url = `${SYSTEM_USERS}/${userId}`
  const options = {
    headers: getHeaders().headers,
    method: "DELETE"
  }

  return await fetch(url, options)
    .then(response => response.json())
    .then(data => {
      if (data.error) {
        throw new Error(data.error.message || "Failed to delete user")
      }
      return data
    })
}

// Vehicles CRUD
export async function getVehicles(id) {
  if (id) {
    return await fetch(VEHICLES + "?filter=id=" + id, getHeaders())
      .then(response => response.json())
      .then(data => data.resource);
  }
  return await fetch(VEHICLES, getHeaders())
    .then(response => response.json())
    .then(data => data.resource);
}

export async function postVehicle(data, config = {}) {
  const options = {
    body: JSON.stringify(data),
    headers: getHeaders().headers,
    method: "POST"
  }
  return await fetch(VEHICLES, options)
    .then(response => response.json())
    .then(data => data);
}

export async function patchVehicle(data, config = {}) {
  const options = {
    body: JSON.stringify(data),
    headers: getHeaders().headers,
    method: "PATCH"
  }
  return await fetch(VEHICLES, options)
    .then(response => response.json())
    .then(data => data);
}

export async function deleteVehicle(data, config = {}) {
  const options = {
    body: JSON.stringify(data),
    headers: getHeaders().headers,
    method: "DELETE"
  }
  return await fetch(VEHICLES, options)
    .then(response => response.json())
    .then(data => data);
}

// Company Details CRUD
export async function getCompanyDetails() {
  return await fetch(COMPANY_DETAILS, getHeaders())
    .then(response => response.json())
    .then(data => data.resource && data.resource[0] ? data.resource[0] : null);
}

export async function postCompanyDetails(data, config = {}) {
  const options = {
    body: JSON.stringify(data),
    headers: getHeaders().headers,
    method: "POST"
  }
  return await fetch(COMPANY_DETAILS, options)
    .then(response => response.json())
    .then(data => data);
}

export async function patchCompanyDetails(data, config = {}) {
  const options = {
    body: JSON.stringify(data),
    headers: getHeaders().headers,
    method: "PATCH"
  }
  return await fetch(COMPANY_DETAILS, options)
    .then(response => response.json())
    .then(data => data);
}

// Expense Codes (ManageCodes) CRUD
export async function getExpenseCodes(id) {
  if (id) {
    return await fetch(GET_CODES + "?filter=id=" + id, getHeaders())
      .then(response => response.json())
      .then(data => data.resource);
  }
  return await fetch(GET_CODES, getHeaders())
    .then(response => response.json())
    .then(data => data.resource);
}

export async function postExpenseCode(data, config = {}) {
  const options = {
    body: JSON.stringify(data),
    headers: getHeaders().headers,
    method: "POST"
  }
  return await fetch(GET_CODES, options)
    .then(response => response.json())
    .then(data => data);
}

export async function patchExpenseCode(data, config = {}) {
  const options = {
    body: JSON.stringify(data),
    headers: getHeaders().headers,
    method: "PATCH"
  }
  return await fetch(GET_CODES, options)
    .then(response => response.json())
    .then(data => data);
}

export async function deleteExpenseCode(data, config = {}) {
  const options = {
    body: JSON.stringify(data),
    headers: getHeaders().headers,
    method: "DELETE"
  }
  return await fetch(GET_CODES, options)
    .then(response => response.json())
    .then(data => data);
}

export async function getPayments(id) {
  if (id) {
    return await fetch(PAYMENTS + "?filter=id=" + id, getHeaders())
      .then(response => response.json())
      .then(data => data.resource);
  }
  if (!id) {
    return await fetch(PAYMENTS, getHeaders())
      .then(response => response.json())
      .then(data => data.resource);
  }
}
export async function getPaymentsByEntity(entityId) {
  if (entityId) {
    return await fetch(PAYMENTS + "?filter=entity_id=" + entityId, getHeaders())
      .then(response => response.json())
      .then(data => data.resource);
  }
  return [];
}
export async function getLoansByCustomer(customerId) {
  if (customerId) {
    return await fetch(CREATE_LOAN + "?filter=customer_id=" + customerId, getHeaders())
      .then(response => response.json())
      .then(data => data.resource);
  }
  return [];
}
export async function getLoans(id) {
  if (id) {
    const response = await fetch(`${CREATE_LOAN}?filter=id=${id}`, getHeaders());
    const data = await response.json();
    return data.resource;
  }

  let allLoans = [];
  let offset = 0;
  let hasNext = true;

  while (hasNext) {
    const response = await fetch(`${CREATE_LOAN}?offset=${offset}`, getHeaders());
    const data = await response.json();

    allLoans = allLoans.concat(data.resource);

    if (data.meta && data.meta.next) {
      offset = data.meta.next;
    } else {
      hasNext = false;
    }
  }

  return allLoans;
}



export const checkOrderStock = async (products) => {
  const status = [];
  const requests = products.map((prd, i) =>
    getProducts(prd.id).then(res => {

      if ((Number(res[0].stock) - Number(prd.quantity)) >= 0) {
        status.push({ stockStatus: "STOCK-OK", quantity: prd.quantity, product: res[0] })
        return "STOCK-OK"
      }
      if ((Number(res[0].stock) - Number(prd.quantity)) < 0) {
        status.push({ stockStatus: "STOCK-FAULT", quantity: Number(prd.quantity), product: res[0] })
        return "STOCK-FAULT"
      }
    })
  )
  return Promise.all(requests).then(() => {
    return status
  });
}

export async function patchMaterial(data, config = {}) {
  const options = {
    body: JSON.stringify(data
    ),
    headers: getHeaders().headers,
    method: "PATCH"
  }
  return await fetch(GET_PRODUCTS, options)
    .then(response => response.json())
    .then(data => data);
}

export async function postInvoice(data, config = {}) {
  const options = {
    body: JSON.stringify(data
    ),
    headers: getHeaders().headers,
    method: "POST"
  }
  return await fetch(INVOICES, options)
    .then(response => response.json())
    .then(data => data);
}
export async function getInvoicesbyproid(id) {
  if (id) {
    return await fetch(INVOICES + "?filter=providerid=" + id, getHeaders())
      .then(response => response.json())
      .then(data => data.resource);
  }
  if (!id) {
    return await fetch(INVOICES, getHeaders())
      .then(response => response.json())
      .then(data => data.resource);
  }
}

export async function getInvoices(id) {
  if (id) {
    return await fetch(INVOICES + "?filter=id=" + id, getHeaders())
      .then(response => response.json())
      .then(data => data.resource);
  }
  if (!id) {
    return await fetch(INVOICES, getHeaders())
      .then(response => response.json())
      .then(data => data.resource);
  }
}


export const fetchInvoicesEcon = async (fromDate, tillDate, selectedCode) => {
  if (!fromDate || !tillDate || !selectedCode) {
    alert("Please fill out all fields before submitting.");
    return;
  }

  // Format dates as YYYY-MM-DD for PostgreSQL compatibility
  const fromDateStr = fromDate; // Already in YYYY-MM-DD format from date input
  const tillDateStr = tillDate;
  const rawFilter = `(importDate>='${fromDateStr}') AND (importDate<='${tillDateStr} 23:59:59') AND (refcode='${selectedCode}')`;
  const encodedFilter = encodeURIComponent(rawFilter);

  const limit = 500;
  let offset = 0;
  let allRecords = [];
  let total = Infinity;

  while (offset < total) {
    const url = `${INVOICES}?filter=${encodedFilter}&limit=${limit}&offset=${offset}&include_count=true`;
    const response = await fetch(url, getHeaders());
    const data = await response.json();

    const records = data.resource || [];
    allRecords = allRecords.concat(records);

    total = data?.meta?.count || allRecords.length + (records.length === limit ? 1 : 0);
    offset += limit;

    if (records.length < limit) break;
    await new Promise(res => setTimeout(res, 1000));
  }

  return allRecords;
};



export async function getProviders(id) {
  if (id) {
    return await fetch(GET_PROVIDERS + "?filter=id=" + id, getHeaders())
      .then(response => response.json())
      .then(data => data.resource);
  }
  if (!id) {
    return await fetch(GET_PROVIDERS, getHeaders())
      .then(response => response.json())
      .then(data => data.resource);
  }
}

export async function getCodes() {
 
    return await fetch(GET_CODES, getHeaders())
      .then(response => response.json())
      .then(data => data.resource);
  
}


export async function calccost(prd) {
  const requirements = JSON.parse(prd.requirements)
  let matesum = []
  return await requirements.map((step, i) => {
    step.materials.map((mats) => {
      getMaterials(mats.id).then(res => {
        const mat = mats.quantity * res[0].costprice;
        matesum.push(mat)
  
        return matesum.reduce((a, b) => a + b, 0)
      })
    })

    if (requirements.length === i + 1) {
      return matesum.reduce((a, b) => a + b, 0)
    }
  })
}



export async function totalCost(steps) {
  const products = await getProducts(null, "MATERIAL");

  for (const prod of products) {
    const matesum = [];
    const prod_requirements = JSON.parse(prod.requirements);
    for (const reqs of prod_requirements) {
      for (const mats of reqs.materials) {
        const res = await getMaterials(mats.id);
       // console.log(mats.quantity)
        const mat = mats.quantity * res[0].costprice;
        // const mat = {material: mats.name, matid:res[0].id ,liveCost: res[0].costprice}
      //  console.log(prod.name)
        matesum.push(mat);
        const final = matesum.reduce((a, b) => a + b, 0);
       // console.log(final)
        const payload = {
          "resource": [
      
            {
              "id":prod.id,
              "costprice":final
            }
          ],
        }
     //   console.log(payload)
        patchMaterial(payload).then(res => {
          if (res && res.error) {
            //console.log(res)
          }
          if (res && res.resource[0]) {
           
            return "updated"
          }
        })
      }
    }
  }
  //const final = matesum.reduce((a, b) => a + b, 0);
 
  //return final;
}

export async function getTypes() {
  return await fetch(GET_TYPES, getHeaders())
    .then(response => response.json())
    .then(data => data.resource);
}

export async function getBanks() {
  return await fetch(GET_BANKS, getHeaders())
    .then(response => response.json())
    .then(data => data.resource);
}


export async function createTaskType(data, config = {}) {
  const options = {
    body: JSON.stringify(data
    ),
    headers: getHeaders().headers,
    method: "POST"
  }
  return await fetch(GET_TYPES, options)
    .then(response => response.json())
    .then(data => data);
}

export async function deleteTaskType(data, config = {}) {
  const options = {
    body: JSON.stringify(data
    ),
    headers: getHeaders().headers,
    method: "DELETE"
  }
  return await fetch(GET_TYPES, options)
    .then(response => response.json())
    .then(data => data);
}

export async function getCustOrders(id) {
  if (id) {
    return await fetch(GET_ORDERS + "?filter=customer_id=" + id, getHeaders())
      .then(response => response.json())
      .then(data => data.resource);
  }

}

export async function getMaterials(id) {
  const baseUrl = GET_PRODUCTS;
  const opts = getHeaders(); // use fresh headers with token

  // If a specific ID is provided, just fetch that one record.
  if (id) {
    const res = await fetch(`${baseUrl}?filter=id=${encodeURIComponent(id)}`, opts);
    const json = await res.json();
    return json.resource ?? [];
  }

  // Otherwise, fetch ALL records across pages.
  const all = [];
  const limit = 1000; // adjust if your DSP enforces a different max
  let offset = 0;
  let total = Infinity;

  try {
    while (all.length < total) {
      const url =
        `${baseUrl}?include_count=true&limit=${limit}&offset=${offset}`;
      const res = await fetch(url, opts);
      if (!res.ok) throw new Error(`HTTP ${res.status}`);

      const json = await res.json();
      const batch = json.resource ?? [];
      const meta = json.meta ?? {};
      // FlexFlow returns meta.total when include_count=true
      if (Number.isFinite(meta.total)) total = meta.total;

      all.push(...batch);

      // advance; also break if the server returned fewer than limit
      offset += limit;
      if (batch.length < limit) break;
    }
  } catch (err) {
    console.error("Error fetching materials:", err);
    // Decide how you want to handle errors (rethrow, return partial, etc.)
  }

  return all;
}




export async function postMaterial(data, config = {}) {
  const options = {
    body: JSON.stringify(data
    ),
    headers: getHeaders().headers,
    method: "POST"
  }
  return await fetch(GET_PRODUCTS, options)
    .then(response => response.json())
    .then(data => data);
}


export async function postTask(data, config = {}) {
  const options = {
    body: JSON.stringify(data
    ),
    headers: getHeaders().headers,
    method: "POST"
  }
  return await fetch(CREATE_TASK, options)
    .then(response => response.json())
    .then(data => data);
}

export async function patchTask(data, config = {}) {
  const options = {
    body: JSON.stringify(data
    ),
    headers: getHeaders().headers,
    method: "PATCH"
  }
  return await fetch(CREATE_TASK, options)
    .then(response => response.json())
    .then(data => data);
}


export async function postLoan(data, config = {}) {
  const options = {
    body: JSON.stringify(data
    ),
    headers: getHeaders().headers,
    method: "POST"
  }
  return await fetch(CREATE_LOAN, options)
    .then(response => response.json())
    .then(data => data);
}


export async function postOrder(data, config = {}) {
  const options = {
    body: JSON.stringify(data
    ),
    headers: getHeaders().headers,
    method: "POST"
  }
  return await fetch(CREATE_ORDER, options)
    .then(response => response.json())
    .then(data => data);
}


export async function postProvider(data, config = {}) {
  const options = {
    body: JSON.stringify(data
    ),
    headers: getHeaders().headers,
    method: "POST"
  }
  return await fetch(GET_PROVIDERS, options)
    .then(response => response.json())
    .then(data => data);
}

export async function postCustomer(data, config = {}) {
  const options = {
    body: JSON.stringify(data
    ),
    headers: getHeaders().headers,
    method: "POST"
  }
  return await fetch(CREATE_CUSTOMER, options)
    .then(response => response.json())
    .then(data => data);
}

export async function patchCustomer(data, config = {}) {
  const options = {
    body: JSON.stringify(data
    ),
    headers: getHeaders().headers,
    method: "PATCH"
  }
  return await fetch(CREATE_CUSTOMER, options)
    .then(response => response.json())
    .then(data => data);
}

export async function postPayment(data, config = {}) {
  const options = {
    body: JSON.stringify(data
    ),
    headers: getHeaders().headers,
    method: "POST"
  }
  return await fetch(PAYMENTS, options)
    .then(response => response.json())
    .then(data => data);
}

export async function patchPayment(data, config = {}) {
  const options = {
    body: JSON.stringify(data),
    headers: getHeaders().headers,
    method: "PATCH"
  }
  return await fetch(PAYMENTS, options)
    .then(response => response.json())
    .then(data => data);
}

export async function deletePayment(id) {
  const options = {
    headers: getHeaders().headers,
    method: "DELETE"
  }
  return await fetch(`${PAYMENTS}/${id}`, options)
    .then(response => response.json())
    .catch(() => { /* error handled */ });
}

export async function postShipping(data, config = {}) {
  const options = {
    body: JSON.stringify(data
    ),
    headers: getHeaders().headers,
    method: "POST"
  }
  return await fetch(SHIPPING, options)
    .then(response => response.json())
    .then(data => data);
}


export async function postProduct(data, config = {}) {
  const options = {
    body: JSON.stringify(data
    ),
    headers: getHeaders().headers,
    method: "POST"
  }
  return await fetch(CREATE_PRODUCT, options)
    .then(response => response.json())
    .then(data => data);
}



export async function patchOrder(data, config = {}) {
  const options = {
    body: JSON.stringify(data),
    headers: getHeaders().headers,
    method: "PATCH"
  }
  return await fetch(CREATE_ORDER, options)
    .then(response => response.json())
    .then(data => data);
}

export async function patchProvider(data, config = {}) {
  const options = {
    body: JSON.stringify(data),
    headers: getHeaders().headers,
    method: "PATCH"
  }
  return await fetch(GET_PROVIDERS, options)
    .then(response => response.json())
    .then(data => data);
}

export async function patchProduct(data, config = {}) {
  const options = {
    body: JSON.stringify(data),
    headers: getHeaders().headers,
    method: "PATCH"
  }
  return await fetch(CREATE_PRODUCT, options)
    .then(response => response.json())
    .then(data => data);
}
export async function updateTaskType(data, config = {}) {
  const options = {
    body: JSON.stringify(data),
    headers: getHeaders().headers,
    method: "PATCH"
  }
  return await fetch(GET_TYPES, options)
    .then(response => response.json())
    .then(data => data);
}
export async function postLogin(data, config = {}) {
  const options = {
    body: JSON.stringify({
      email: data.email,
      password: data.password,
    }),
    headers: {
      Accept: "application/json",
      "Content-Type": "application/json",
      "X-Api-Key": APP_API_KEY,
    },
    method: "POST"
  }
  return await fetch(POST_LOGIN, options)
    .then(response => response.json())
    .then(data => data);
}

/**
 * Login for user authentication
 * Uses the system endpoint: /api/v2/user/session
 *
 * @param {Object} credentials - Login credentials
 * @param {string} credentials.email - User email
 * @param {string} credentials.password - User password
 * @returns {Promise<Object>} Login response with session_token and user info
 */
export async function postAppLogin(credentials) {
  const url = `${FLEXFLOW}user/session`

  const options = {
    body: JSON.stringify({
      email: credentials.email,
      password: credentials.password,
    }),
    headers: {
      Accept: "application/json",
      "Content-Type": "application/json",
      "X-Api-Key": APP_API_KEY,
    },
    method: "POST"
  }

  return await fetch(url, options)
    .then(response => response.json())
    .then(data => data)
}

export async function searchProductsByText(text, limit = 20) {
  if (!text || !text.trim()) return []
  const s = text.trim()
  const filter = encodeURIComponent(`(sku like %${s}%) OR (name like %${s}%)`)
  const url = `${GET_PRODUCTS}?filter=${filter}&limit=${limit}&include_count=true`
  const res = await fetch(url, { headers: getHeaders().headers, method: "GET" })
  const data = await res.json()
  return data?.resource || []
}

export async function getProductsBySKUs(skus) {
  if (!skus || skus.length === 0) return []
  const skuList = skus.map(s => `'${String(s).replace(/'/g, "''")}'`).join(",")
  const url = `${GET_PRODUCTS}?filter=${encodeURIComponent(`sku IN (${skuList})`)}&include_count=true`
  const res = await fetch(url, { headers: getHeaders().headers, method: "GET" })
  const data = await res.json()
  return data?.resource || []
}

export async function getProducts(id, type) {
  const all = [];
  let offset = 0;
  let baseUrl = GET_PRODUCTS;

  // Add filters
  if (id) {
    baseUrl += `?filter=id=${encodeURIComponent(id)}`;
  } else if (type) {
    baseUrl += `?filter=usageType=${encodeURIComponent(type)}`;
  }

  // Always request count so meta.next is included
  const sep = baseUrl.includes('?') ? '&' : '?';
  baseUrl += `${sep}include_count=true`;

  let url = baseUrl;

  while (true) {
    const res = await fetch(url + `&offset=${offset}`, getHeaders());
    const data = await res.json();

    if (Array.isArray(data?.resource)) {
      all.push(...data.resource);
    }

    if (!data?.meta?.next) break; // no more pages

    offset = data.meta.next; // next offset
  }

  return all;
}


/**
 * Server-side customer search by name or VAT (AFM). Returns up to `limit`
 * matches. Empty query returns the first `limit` rows.
 * Used by the Quote app's customer picker — replaces fetching all 16K rows.
 */
export async function searchCustomers(query = "", limit = 50) {
  const fields = "id,name,vat,phone,address,city,email,label,profession"
  const q = String(query || "").trim().replace(/[%(),]/g, "")
  const params = new URLSearchParams({ limit: String(limit), order: "name ASC", fields })
  if (q) {
    params.set("filter", `(name like %${q}%) OR (vat like %${q}%) OR (phone like %${q}%) OR (label like %${q}%)`)
  }
  const url = `${GET_CUSTOMERS}?${params.toString()}`
  const res = await fetch(url, getHeaders())
  const data = await res.json()
  return Array.isArray(data?.resource) ? data.resource : []
}

export async function getCustomers(getfull) {
  let url = GET_CUSTOMERS;
  if (getfull) {
    url = `${GET_CUSTOMERS}?related=${encodeURIComponent('orders,payments')}`;
  }
  return await fetch(url, getHeaders())
    .then(response => response.json())
    .then(data => {
      if (data.error) {
        return data.error.code
      }
      return data.resource
    });
}

///TODOOOOO 
export async function getCustomerDetail(id) {
  const url = `${GET_CUSTOMERS}?related=${encodeURIComponent('orders,payments')}&filter=id=${id}`;
  return await fetch(url, getHeaders())
  .then(response => response.json())
  .then(data => data.resource);
}
export async function getCustomerInfo(id) {
  const url = `${GET_CUSTOMERS}?&filter=id=${id}`;
  return await fetch(url, getHeaders())
  .then(response => response.json())
  .then(data => data.resource);
}

export async function getOrders(exclude_shipped = false) {
  let url = GET_ORDERS;
  
  if (exclude_shipped) {
    url += "?filter=status!=SHIPPED";
  }

  return await fetch(url, getHeaders())
    .then(response => response.json())
    .then(data => data.resource);
}



export const fetchPaymentsDate = async (fromDate, tillDate) => {
  // Format dates as YYYY-MM-DD for PostgreSQL compatibility
  const fromDateStr = fromDate; // Already in YYYY-MM-DD format from date input
  const tillDateStr = tillDate;
  const rawFilter = `(date>='${fromDateStr}') AND (date<='${tillDateStr} 23:59:59')`;
  const encodedFilter = encodeURIComponent(rawFilter);

  const limit = 500;
  let offset = 0;
  let allRecords = [];
  let total = Infinity;

  while (offset < total) {
    const url = `${PAYMENTS}?filter=${encodedFilter}&limit=${limit}&offset=${offset}&include_count=true`;
    const response = await fetch(url, getHeaders());
    const data = await response.json();

    const records = data.resource || [];
    allRecords = allRecords.concat(records);

    total = data?.meta?.count || allRecords.length + (records.length === limit ? 1 : 0);
    offset += limit;

    if (records.length < limit) break;
    await new Promise(res => setTimeout(res, 1000));
  }

  return allRecords;
};


export async function fetchOrdersDate(fromDate, tillDate, status) {
  // Use updated field for filtering (when order was shipped/modified)
  // Format dates as YYYY-MM-DD for PostgreSQL compatibility
  const fromDateStr = fromDate; // Already in YYYY-MM-DD format from date input
  const tillDateStr = tillDate;

  // Build filter using updated field which is more reliably populated
  let rawFilter = `(updated>='${fromDateStr}') AND (updated<='${tillDateStr} 23:59:59')`;
  if (status) {
    rawFilter += ` AND (status='${status}')`;
  }

  const encodedFilter = encodeURIComponent(rawFilter);

  const limit = 500;
  let offset = 0;
  let allRecords = [];
  let total = Infinity;

  while (offset < total) {
    const url = `${GET_ORDERS}?filter=${encodedFilter}&limit=${limit}&offset=${offset}&include_count=true`;
    const response = await fetch(url, getHeaders());
    const data = await response.json();

    const records = data.resource || [];
    allRecords = allRecords.concat(records);

    total = data?.meta?.total_count || allRecords.length + (records.length === limit ? 1 : 0);
    offset += limit;

    if (records.length < limit) break;
    await new Promise(res => setTimeout(res, 1000));
  }

  return allRecords;
}

/**
 * Get economics summary from backend (server-side calculations)
 * This is much faster than fetching all raw data and calculating client-side
 */
export const getEconomicsSummary = async (fromDate, tillDate) => {
  const url = `${FLEXFLOW}economics/summary?fromDate=${fromDate}&tillDate=${tillDate}`;
  const response = await fetch(url, getHeaders());
  if (!response.ok) {
    throw new Error(`Economics API error: ${response.status}`);
  }
  return response.json();
};

export async function getOrdersbyid(id) {
  return await fetch(GET_ORDERS + "?filter=id=" + id, getHeaders())
    .then(response => response.json())
    .then(data => data.resource);
}
export async function getOrdersbystatus(status) {
  return await fetch(GET_ORDERS + "?filter=status=" + status, getHeaders())
    .then(response => response.json())
    .then(data => data.resource);
}
export async function getShipments() {
  return await fetch(SHIPPING, getHeaders())
    .then(response => response.json())
    .then(data => data.resource);
}


export async function patchShipment(data, config = {}) {
  const options = {
    body: JSON.stringify(data),
    headers: getHeaders().headers,
    method: "PATCH"
  }
  return await fetch(SHIPPING, options)
    .then(response => response.json())
    .then(data => data);
}

export async function getShipmentsDriver(driverId) {
  // Filter shipments by driver_id field (not by driver name)
  const url = `${SHIPPING}?filter=(driver_id=${encodeURIComponent(driverId)})`;
  try {
    const response = await fetch(url, getHeaders());
    const data = await response.json();
    return data.resource;
  } catch (error) {
    console.error("Error fetching shipments:", error);
    return [];
  }
}


export async function getTasks() {
  return await fetch(GET_TASKS, getHeaders())
    .then(response => response.json())
    .then(data => data.resource);
}

// Get ALL tasks including FINISHED (for analytics/progress tracking)
export async function getAllTasks() {
  return await fetch(GET_ALL_TASKS, getHeaders())
    .then(response => response.json())
    .then(data => data.resource);
}
export async function getTasksbyType(type) {
  return await fetch(GET_TASKS + " AND (type="+type+")", getHeaders())
    .then(response => response.json())
    .then(data => data.resource);
}

export async function getTasksby(id) {
  return await fetch(GET_TASKS_OF_ORDER + "?filter=orderref=" + id, getHeaders())
    .then(response => response.json())
    .then(data => data.resource);
}


export async function get(url, config = {}) {

}




export async function post(url, data, config = {}) {

}






// axiosApi.defaults.headers.common["Authorization"] = token
// axiosApi.interceptors.response.use(
//   response => response,
//   error => Promise.reject(error)
// )

// ============================================================================
// DEMO DATA GENERATION
// ============================================================================

/**
 * Generate demo payment records for customers that have orders
 * Creates realistic payment data based on order totals
 *
 * @param {Function} onProgress - Optional callback for progress updates (current, total, message)
 * @returns {Promise<{created: number, errors: number, details: Array}>}
 */
export async function generateDemoPayments(onProgress = () => {}) {
  const results = { created: 0, errors: 0, details: [] }

  try {
    // Get customers with their orders
    onProgress(0, 100, "Fetching customers with orders...")
    const customers = await getCustomers(true) // true = include related orders/payments

    if (!customers || !Array.isArray(customers)) {
      throw new Error("Failed to fetch customers")
    }

    // Filter customers that have orders
    const customersWithOrders = customers.filter(c =>
      c.orders_by_customer_id && c.orders_by_customer_id.length > 0
    )

    if (customersWithOrders.length === 0) {
      return { ...results, details: [{ message: "No customers with orders found" }] }
    }

    const total = customersWithOrders.length
    let current = 0

    // Payment types to randomly assign
    const paymentTypes = ["CASH", "CARD", "BANK", "CHECK"]
    const refcodes = {
      CASH: "38.00.00",
      CARD: "38.00.00",
      CHECK: "38.01.00",
      BANK: "38.02.00"
    }

    // Sample bank names for CHECK and BANK payments
    const bankNames = ["Alpha Bank", "Eurobank", "National Bank", "Piraeus Bank", "Attica Bank"]

    for (const customer of customersWithOrders) {
      current++
      onProgress(current, total, `Processing ${customer.name}...`)

      try {
        // Calculate total order value for this customer
        const orders = customer.orders_by_customer_id || []
        let totalOrderValue = 0

        for (const order of orders) {
          try {
            const totals = JSON.parse(order.totals || "{}")
            totalOrderValue += Number(totals.totalOrder) || 0
          } catch (e) {
            // Skip invalid totals
          }
        }

        if (totalOrderValue <= 0) {
          results.details.push({ customer: customer.name, message: "No order value" })
          continue
        }

        // Check existing payments for this customer
        const existingPayments = customer.payments_by_customer_id || []
        const existingTotal = existingPayments.reduce((sum, p) => sum + (Number(p.ammount) || 0), 0)

        // Calculate remaining balance
        const remainingBalance = totalOrderValue - existingTotal

        if (remainingBalance <= 0) {
          results.details.push({ customer: customer.name, message: "Already paid in full" })
          continue
        }

        // Create 1-3 payments for variety
        const numPayments = Math.min(3, Math.ceil(remainingBalance / 500))
        let remainingToPay = remainingBalance

        for (let i = 0; i < numPayments && remainingToPay > 0; i++) {
          // Random payment amount (30-100% of remaining, or full amount for last payment)
          const isLastPayment = i === numPayments - 1
          const paymentPercent = isLastPayment ? 1 : (0.3 + Math.random() * 0.4)
          const paymentAmount = isLastPayment
            ? remainingToPay
            : Math.round(remainingToPay * paymentPercent * 100) / 100

          // Random payment type
          const paymentType = paymentTypes[Math.floor(Math.random() * paymentTypes.length)]

          // Random date in the last 60 days
          const daysAgo = Math.floor(Math.random() * 60)
          const paymentDate = new Date()
          paymentDate.setDate(paymentDate.getDate() - daysAgo)

          // Build payment data
          const paymentData = {
            bound: "Inbound",
            type: paymentType,
            ammount: paymentAmount,
            date: paymentDate.toISOString(),
            entity_id: customer.id,
            entity_name: customer.name,
            series: `PAY-${customer.id}-${Date.now()}-${i}`,
            refcode: refcodes[paymentType]
          }

          // Add type-specific fields
          if (paymentType === "CHECK") {
            const expireDate = new Date()
            expireDate.setDate(expireDate.getDate() + 30 + Math.floor(Math.random() * 60))
            paymentData.bankname = bankNames[Math.floor(Math.random() * bankNames.length)]
            paymentData.issuer = customer.name
            paymentData.expire_date = expireDate.toISOString()
          } else if (paymentType === "BANK") {
            paymentData.bankname = bankNames[Math.floor(Math.random() * bankNames.length)]
            paymentData.iban = `GR${Math.floor(1000000000000000 + Math.random() * 9000000000000000)}`
            paymentData.receipt_code = `RCP${Date.now()}`
          }

          // Create payment
          const response = await postPayment({ resource: [paymentData] })

          if (response && response.resource) {
            results.created++
            results.details.push({
              customer: customer.name,
              amount: paymentAmount,
              type: paymentType,
              message: "Created"
            })
          } else if (response && response.error) {
            results.errors++
            results.details.push({
              customer: customer.name,
              message: `Error: ${response.error.message}`
            })
          }

          remainingToPay -= paymentAmount

          // Small delay to avoid overwhelming the API
          await new Promise(resolve => setTimeout(resolve, 100))
        }
      } catch (err) {
        results.errors++
        results.details.push({ customer: customer.name, message: `Error: ${err.message}` })
      }
    }

    onProgress(total, total, "Complete!")
    return results

  } catch (error) {
    console.error("generateDemoPayments error:", error)
    throw error
  }
}

// ============================================================================
// END DEMO DATA GENERATION
// ============================================================================

// ============================================================================
// MYAADE INTEGRATION - VAT VALIDATION CREDENTIALS
// ============================================================================

// Backend base URL for MyAade endpoints (dev fork -> Eliton dev EC2)
const BACKEND_URL = "https://devserver.eliton.net"

/**
 * Get MyAade credentials status (does not return the actual password)
 * @returns {Promise<{configured: boolean, username: string|null}>}
 */
export async function getMyAadeCredentials() {
  try {
    const response = await fetch(`${BACKEND_URL}/api/v2/system/myaade`, {
      method: "GET",
      ...getHeaders()
    })

    if (!response.ok) {
      throw new Error("Failed to fetch MyAade credentials")
    }

    return await response.json()
  } catch (error) {
    console.error("getMyAadeCredentials error:", error)
    return { configured: false, username: null }
  }
}

/**
 * Save MyAade credentials (password is encrypted server-side)
 * @param {Object} credentials
 * @param {string} credentials.username - MyAade username
 * @param {string} [credentials.password] - MyAade password (optional if updating username only)
 * @returns {Promise<{success: boolean, message?: string}>}
 */
export async function saveMyAadeCredentials(credentials) {
  const response = await fetch(`${BACKEND_URL}/api/v2/system/myaade`, {
    method: "POST",
    headers: {
      ...getHeaders().headers,
      "Content-Type": "application/json"
    },
    body: JSON.stringify(credentials)
  })

  if (!response.ok) {
    const error = await response.json()
    throw new Error(error.message || "Failed to save MyAade credentials")
  }

  return await response.json()
}

/**
 * Test MyAade connection using stored credentials
 * @returns {Promise<{success: boolean, message?: string}>}
 */
export async function testMyAadeConnection() {
  const response = await fetch(`${BACKEND_URL}/api/v2/system/myaade/test`, {
    method: "POST",
    ...getHeaders()
  })

  if (!response.ok) {
    const error = await response.json()
    throw new Error(error.message || "Connection test failed")
  }

  return await response.json()
}

// ============================================================================
// END MYAADE INTEGRATION
// ============================================================================

// ============================================================================
// INVOICE COMPANIES - Multi-Company Invoicing
// ============================================================================

/**
 * Get all invoice companies
 * @returns {Promise<Array>} Array of invoice companies
 */
export async function getInvoiceCompanies() {
  try {
    const response = await fetch(INVOICE_COMPANIES, {
      method: "GET",
      headers: getHeaders().headers
    })

    if (!response.ok) {
      throw new Error("Failed to fetch invoice companies")
    }

    const data = await response.json()
    return data.resource || []
  } catch (error) {
    console.error("getInvoiceCompanies error:", error)
    throw error
  }
}

/**
 * Get single invoice company by ID
 * @param {number} id - Invoice company ID
 * @returns {Promise<Object>} Invoice company data
 */
export async function getInvoiceCompanyById(id) {
  try {
    const response = await fetch(`${INVOICE_COMPANIES}/${id}`, {
      method: "GET",
      headers: getHeaders().headers
    })

    if (!response.ok) {
      throw new Error("Failed to fetch invoice company")
    }

    const data = await response.json()
    return data.resource?.[0] || null
  } catch (error) {
    console.error("getInvoiceCompanyById error:", error)
    throw error
  }
}

/**
 * Get decrypted invoice company credentials (for invoicing operations)
 * @param {number} id - Invoice company ID
 * @returns {Promise<Object>} Decrypted invoice company data
 */
export async function getInvoiceCompanyDecrypted(id) {
  try {
    const response = await fetch(`${INVOICE_COMPANIES}/${id}/decrypted`, {
      method: "GET",
      headers: getHeaders().headers
    })

    if (!response.ok) {
      throw new Error("Failed to fetch invoice company credentials")
    }

    const data = await response.json()
    return data.resource?.[0] || null
  } catch (error) {
    console.error("getInvoiceCompanyDecrypted error:", error)
    throw error
  }
}

/**
 * Create new invoice company
 * @param {Object} data - Invoice company data
 * @returns {Promise<Object>} Created invoice company
 */
export async function createInvoiceCompany(data) {
  try {
    const response = await fetch(INVOICE_COMPANIES, {
      method: "POST",
      headers: getHeaders().headers,
      body: JSON.stringify({ resource: [data] })
    })

    if (!response.ok) {
      const error = await response.json()
      throw new Error(error.error?.message || "Failed to create invoice company")
    }

    const result = await response.json()
    return result.resource?.[0] || result
  } catch (error) {
    console.error("createInvoiceCompany error:", error)
    throw error
  }
}

/**
 * Update invoice company
 * @param {number} id - Invoice company ID
 * @param {Object} data - Updated invoice company data
 * @returns {Promise<Object>} Updated invoice company
 */
export async function updateInvoiceCompany(id, data) {
  try {
    const response = await fetch(`${INVOICE_COMPANIES}/${id}`, {
      method: "PATCH",
      headers: getHeaders().headers,
      body: JSON.stringify({ resource: [data] })
    })

    if (!response.ok) {
      const error = await response.json()
      throw new Error(error.error?.message || "Failed to update invoice company")
    }

    const result = await response.json()
    return result.resource?.[0] || result
  } catch (error) {
    console.error("updateInvoiceCompany error:", error)
    throw error
  }
}

/**
 * Delete invoice company (soft delete by default)
 * @param {number} id - Invoice company ID
 * @param {boolean} hard - If true, permanently delete
 * @returns {Promise<Object>} Deletion result
 */
export async function deleteInvoiceCompany(id, hard = false) {
  try {
    const url = hard ? `${INVOICE_COMPANIES}/${id}?hard=true` : `${INVOICE_COMPANIES}/${id}`
    const response = await fetch(url, {
      method: "DELETE",
      headers: getHeaders().headers
    })

    if (!response.ok) {
      const error = await response.json()
      throw new Error(error.error?.message || "Failed to delete invoice company")
    }

    return await response.json()
  } catch (error) {
    console.error("deleteInvoiceCompany error:", error)
    throw error
  }
}

/**
 * Test AADE/e-timologiera connection for invoice company
 * @param {number} id - Invoice company ID
 * @returns {Promise<Object>} Test result
 */
export async function testInvoiceCompanyAade(id) {
  try {
    const response = await fetch(`${INVOICE_COMPANIES}/${id}/test-aade`, {
      method: "POST",
      headers: getHeaders().headers
    })

    if (!response.ok) {
      const error = await response.json()
      throw new Error(error.error?.message || "AADE connection test failed")
    }

    return await response.json()
  } catch (error) {
    console.error("testInvoiceCompanyAade error:", error)
    throw error
  }
}

/**
 * Test Epsilon connection for invoice company
 * @param {number} id - Invoice company ID
 * @returns {Promise<Object>} Test result
 */
export async function testInvoiceCompanyEpsilon(id) {
  try {
    const response = await fetch(`${INVOICE_COMPANIES}/${id}/test-epsilon`, {
      method: "POST",
      headers: getHeaders().headers
    })

    if (!response.ok) {
      const error = await response.json()
      throw new Error(error.error?.message || "Epsilon connection test failed")
    }

    return await response.json()
  } catch (error) {
    console.error("testInvoiceCompanyEpsilon error:", error)
    throw error
  }
}

// ============================================================================
// END INVOICE COMPANIES
// ============================================================================

// ============================================================================
// WORKFLOW API
// ============================================================================

/**
 * Get default workflow template
 * @returns {Promise<Object>} Workflow template with statuses and transitions
 */
export async function getWorkflowConfig() {
  try {
    const response = await fetch(`${FLEXFLOW}workflow/default`, {
      method: "GET",
      headers: getHeaders().headers
    })

    if (!response.ok) {
      const error = await response.json()
      throw new Error(error.error?.message || "Failed to fetch workflow config")
    }

    return await response.json()
  } catch (error) {
    console.error("getWorkflowConfig error:", error)
    throw error
  }
}

/**
 * Get all workflow templates
 * @returns {Promise<Array>} List of workflow templates
 */
export async function getWorkflowTemplates() {
  try {
    const response = await fetch(`${FLEXFLOW}workflow/templates`, {
      method: "GET",
      headers: getHeaders().headers
    })

    if (!response.ok) {
      const error = await response.json()
      throw new Error(error.error?.message || "Failed to fetch workflow templates")
    }

    return await response.json()
  } catch (error) {
    console.error("getWorkflowTemplates error:", error)
    throw error
  }
}

/**
 * Update workflow template
 * @param {number} templateId - Template ID
 * @param {Object} config - Template data with statuses and transitions
 * @returns {Promise<Object>} Updated template
 */
export async function updateWorkflowConfig(templateId, config) {
  try {
    const response = await fetch(`${FLEXFLOW}workflow/templates/${templateId}`, {
      method: "PATCH",
      headers: getHeaders().headers,
      body: JSON.stringify(config)
    })

    if (!response.ok) {
      const error = await response.json()
      throw new Error(error.error?.message || "Failed to update workflow config")
    }

    return await response.json()
  } catch (error) {
    console.error("updateWorkflowConfig error:", error)
    throw error
  }
}

/**
 * Get available transitions for an order
 * @param {number} orderId - Order ID
 * @returns {Promise<Array>} Available transitions with conditions status
 */
export async function getOrderTransitions(orderId) {
  try {
    const response = await fetch(`${FLEXFLOW}workflow/orders/${orderId}/transitions`, {
      method: "GET",
      headers: getHeaders().headers
    })

    if (!response.ok) {
      const error = await response.json()
      throw new Error(error.error?.message || "Failed to fetch transitions")
    }

    return await response.json()
  } catch (error) {
    console.error("getOrderTransitions error:", error)
    throw error
  }
}

/**
 * Execute a transition on an order
 * @param {number} orderId - Order ID
 * @param {number} transitionId - Transition ID
 * @param {Object} options - Optional transition data (notes, etc.)
 * @returns {Promise<Object>} Transition result
 */
export async function executeTransition(orderId, transitionId, options = {}) {
  try {
    const response = await fetch(`${FLEXFLOW}workflow/orders/${orderId}/transitions/${transitionId}`, {
      method: "POST",
      headers: getHeaders().headers,
      body: JSON.stringify(options)
    })

    if (!response.ok) {
      const error = await response.json()
      throw new Error(error.error?.message || "Failed to execute transition")
    }

    return await response.json()
  } catch (error) {
    console.error("executeTransition error:", error)
    throw error
  }
}

// ============================================================================
// END WORKFLOW API
// ============================================================================

// ============================================================================
// CHAT & NOTIFICATIONS API
// ============================================================================

/**
 * Get chat messages for a conversation
 * @param {string} conversationId - User ID of the other participant
 * @returns {Promise<Array>} Messages
 */
export async function getChatMessages(conversationId) {
  try {
    const response = await fetch(`${FLEXFLOW}chat/messages/${conversationId}`, {
      method: "GET",
      headers: getHeaders().headers
    })

    if (!response.ok) {
      const error = await response.json()
      throw new Error(error.error?.message || "Failed to fetch messages")
    }

    return await response.json()
  } catch (error) {
    console.error("getChatMessages error:", error)
    throw error
  }
}

/**
 * Send a chat message
 * @param {Object} message - Message object with recipientId and content
 * @returns {Promise<Object>} Sent message
 */
export async function postChatMessage(message) {
  try {
    const response = await fetch(`${FLEXFLOW}chat/messages`, {
      method: "POST",
      headers: getHeaders().headers,
      body: JSON.stringify(message)
    })

    if (!response.ok) {
      const error = await response.json()
      throw new Error(error.error?.message || "Failed to send message")
    }

    return await response.json()
  } catch (error) {
    console.error("postChatMessage error:", error)
    throw error
  }
}

/**
 * Get notifications for current user
 * @returns {Promise<Array>} Notifications
 */
export async function getNotifications() {
  try {
    const response = await fetch(`${FLEXFLOW}notifications`, {
      method: "GET",
      headers: getHeaders().headers
    })

    if (!response.ok) {
      const error = await response.json()
      throw new Error(error.error?.message || "Failed to fetch notifications")
    }

    return await response.json()
  } catch (error) {
    console.error("getNotifications error:", error)
    throw error
  }
}

/**
 * Mark a notification as read
 * @param {string} notificationId - Notification ID
 * @returns {Promise<Object>} Updated notification
 */
export async function markNotificationAsRead(notificationId) {
  try {
    const response = await fetch(`${FLEXFLOW}notifications/${notificationId}/read`, {
      method: "PUT",
      headers: getHeaders().headers
    })

    if (!response.ok) {
      const error = await response.json()
      throw new Error(error.error?.message || "Failed to mark notification as read")
    }

    return await response.json()
  } catch (error) {
    console.error("markNotificationAsRead error:", error)
    throw error
  }
}

/**
 * Mark all notifications as read
 * @returns {Promise<Object>} Result
 */
export async function markAllNotificationsAsRead() {
  try {
    const response = await fetch(`${FLEXFLOW}notifications/read-all`, {
      method: "PUT",
      headers: getHeaders().headers
    })

    if (!response.ok) {
      const error = await response.json()
      throw new Error(error.error?.message || "Failed to mark all notifications as read")
    }

    return await response.json()
  } catch (error) {
    console.error("markAllNotificationsAsRead error:", error)
    throw error
  }
}

/**
 * Get online users for chat
 * @returns {Promise<Array>} Online users
 */
export async function getOnlineUsers() {
  try {
    const response = await fetch(`${FLEXFLOW}chat/users`, {
      method: "GET",
      headers: getHeaders().headers
    })

    if (!response.ok) {
      const error = await response.json()
      throw new Error(error.error?.message || "Failed to fetch online users")
    }

    return await response.json()
  } catch (error) {
    console.error("getOnlineUsers error:", error)
    throw error
  }
}

// ============================================================================
// END CHAT & NOTIFICATIONS API
// ============================================================================

// ============================================================================
// VAT CATEGORIES API
// ============================================================================

/**
 * Get all VAT categories
 * @returns {Promise<Array>} VAT categories
 */
export async function getVatCategories() {
  try {
    const response = await fetch(VAT_CATEGORIES, {
      method: "GET",
      headers: getHeaders().headers
    })

    if (!response.ok) {
      throw new Error("Failed to fetch VAT categories")
    }

    const data = await response.json()
    return data.resource || []
  } catch (error) {
    console.error("getVatCategories error:", error)
    throw error
  }
}

/**
 * Create VAT category
 * @param {Object} data - VAT category data
 * @returns {Promise<Object>} Created VAT category
 */
export async function createVatCategory(data) {
  try {
    const response = await fetch(VAT_CATEGORIES, {
      method: "POST",
      headers: getHeaders().headers,
      body: JSON.stringify({ resource: [data] })
    })

    if (!response.ok) {
      const error = await response.json()
      throw new Error(error.error?.message || "Failed to create VAT category")
    }

    const result = await response.json()
    return result.resource?.[0] || result
  } catch (error) {
    console.error("createVatCategory error:", error)
    throw error
  }
}

/**
 * Update VAT category
 * @param {number} id - VAT category ID
 * @param {Object} data - Updated VAT category data
 * @returns {Promise<Object>} Updated VAT category
 */
export async function updateVatCategory(id, data) {
  try {
    const response = await fetch(`${VAT_CATEGORIES}/${id}`, {
      method: "PATCH",
      headers: getHeaders().headers,
      body: JSON.stringify({ resource: [data] })
    })

    if (!response.ok) {
      const error = await response.json()
      throw new Error(error.error?.message || "Failed to update VAT category")
    }

    const result = await response.json()
    return result.resource?.[0] || result
  } catch (error) {
    console.error("updateVatCategory error:", error)
    throw error
  }
}

/**
 * Delete VAT category
 * @param {number} id - VAT category ID
 * @returns {Promise<Object>} Deletion result
 */
export async function deleteVatCategory(id) {
  try {
    const response = await fetch(`${VAT_CATEGORIES}/${id}`, {
      method: "DELETE",
      headers: getHeaders().headers
    })

    if (!response.ok) {
      const error = await response.json()
      throw new Error(error.error?.message || "Failed to delete VAT category")
    }

    return await response.json()
  } catch (error) {
    console.error("deleteVatCategory error:", error)
    throw error
  }
}

// ============================================================================
// END VAT CATEGORIES API
// ============================================================================

// ============================================================================
// FILE STORAGE API
// ============================================================================

// Files endpoint base URL (app-specific)
const FILES_ENDPOINT = FLEXFLOW + "files"

/**
 * Upload a file to the file storage system
 *
 * @param {File} file - The file to upload
 * @param {Object} options - Upload options
 * @param {string} options.folder - Folder path (default: '/')
 * @param {string} options.entity_type - Entity type (e.g., 'product', 'order', 'customer')
 * @param {number} options.entity_id - Entity ID to associate the file with
 * @param {boolean} options.is_public - Whether the file is publicly accessible
 * @param {Function} options.onProgress - Progress callback (percentage)
 * @returns {Promise<Object>} Uploaded file metadata
 *
 * @example
 * const result = await uploadFile(imageFile, {
 *   folder: '/products',
 *   entity_type: 'product',
 *   entity_id: 123
 * })
 */
export async function uploadFile(file, options = {}) {
  const {
    folder = '/',
    entity_type = null,
    entity_id = null,
    is_public = false,
    onProgress = null
  } = options

  const formData = new FormData()
  formData.append('file', file)
  formData.append('folder', folder)
  if (entity_type) formData.append('entity_type', entity_type)
  if (entity_id) formData.append('entity_id', String(entity_id))
  formData.append('is_public', String(is_public))

  // Get auth headers (without Content-Type - browser will set it with boundary)
  const authUser = JSON.parse(localStorage.getItem('authUser'))
  const token = authUser ? authUser.session_token : ""

  try {
    // Use XMLHttpRequest for progress tracking if onProgress is provided
    if (onProgress) {
      return new Promise((resolve, reject) => {
        const xhr = new XMLHttpRequest()

        xhr.upload.addEventListener('progress', (event) => {
          if (event.lengthComputable) {
            const percentage = Math.round((event.loaded / event.total) * 100)
            onProgress(percentage)
          }
        })

        xhr.addEventListener('load', () => {
          if (xhr.status >= 200 && xhr.status < 300) {
            const result = JSON.parse(xhr.responseText)
            resolve(result.resource || result)
          } else {
            try {
              const error = JSON.parse(xhr.responseText)
              reject(new Error(error.error?.message || 'Upload failed'))
            } catch {
              reject(new Error(`Upload failed: ${xhr.status}`))
            }
          }
        })

        xhr.addEventListener('error', () => {
          reject(new Error('Network error during upload'))
        })

        xhr.open('POST', `${FILES_ENDPOINT}/upload`)
        xhr.setRequestHeader('Authorization', token ? `Bearer ${token}` : '')
        xhr.send(formData)
      })
    }

    // Standard fetch for uploads without progress tracking
    const response = await fetch(`${FILES_ENDPOINT}/upload`, {
      method: 'POST',
      headers: {
        Authorization: token ? `Bearer ${token}` : ''
      },
      body: formData
    })

    if (!response.ok) {
      const error = await response.json()
      throw new Error(error.error?.message || 'Failed to upload file')
    }

    const result = await response.json()
    return result.resource || result
  } catch (error) {
    console.error('uploadFile error:', error)
    throw error
  }
}

/**
 * Download a file from the file storage system
 *
 * @param {number} fileId - The file ID to download
 * @param {string} filename - Optional filename for the download (defaults to original filename)
 * @returns {Promise<Blob>} The file as a Blob
 *
 * @example
 * const blob = await downloadFile(123)
 * const url = URL.createObjectURL(blob)
 * window.open(url)
 */
export async function downloadFile(fileId, filename = null) {
  try {
    const response = await fetch(`${FILES_ENDPOINT}/${fileId}/download`, {
      method: 'GET',
      headers: getHeaders().headers
    })

    if (!response.ok) {
      const error = await response.json()
      throw new Error(error.error?.message || 'Failed to download file')
    }

    const blob = await response.blob()

    // If filename is provided, trigger a download
    if (filename) {
      const url = URL.createObjectURL(blob)
      const a = document.createElement('a')
      a.href = url
      a.download = filename
      document.body.appendChild(a)
      a.click()
      document.body.removeChild(a)
      URL.revokeObjectURL(url)
    }

    return blob
  } catch (error) {
    console.error('downloadFile error:', error)
    throw error
  }
}

/**
 * Get a file URL (for display in img tags, etc.)
 * For S3 storage, returns a signed URL. For local storage, returns the download endpoint.
 *
 * @param {number} fileId - The file ID
 * @param {number} expiresIn - URL expiration time in seconds (default: 3600)
 * @returns {Promise<Object>} Object containing url, expires_in, and storage_type
 *
 * @example
 * const { url } = await getFileUrl(123)
 * // Use url in an img src or anchor href
 */
export async function getFileUrl(fileId, expiresIn = 3600) {
  try {
    const response = await fetch(`${FILES_ENDPOINT}/${fileId}/url?expires_in=${expiresIn}`, {
      method: 'GET',
      headers: getHeaders().headers
    })

    if (!response.ok) {
      const error = await response.json()
      throw new Error(error.error?.message || 'Failed to get file URL')
    }

    const result = await response.json()
    return result.resource || result
  } catch (error) {
    console.error('getFileUrl error:', error)
    throw error
  }
}

/**
 * Get file metadata
 *
 * @param {number} fileId - The file ID
 * @returns {Promise<Object>} File metadata
 */
export async function getFileById(fileId) {
  try {
    const response = await fetch(`${FILES_ENDPOINT}/${fileId}`, {
      method: 'GET',
      headers: getHeaders().headers
    })

    if (!response.ok) {
      const error = await response.json()
      throw new Error(error.error?.message || 'Failed to get file')
    }

    const result = await response.json()
    return result.resource || result
  } catch (error) {
    console.error('getFileById error:', error)
    throw error
  }
}

/**
 * List files with filtering and pagination
 *
 * @param {Object} options - List options
 * @param {string} options.folder - Filter by folder path
 * @param {string} options.entity_type - Filter by entity type
 * @param {number} options.entity_id - Filter by entity ID
 * @param {number} options.limit - Number of records per page (default: 50)
 * @param {number} options.offset - Starting position (default: 0)
 * @param {boolean} options.include_deleted - Include soft-deleted files
 * @returns {Promise<Object>} Object containing files array and pagination meta
 *
 * @example
 * // Get all files for a product
 * const { resource, meta } = await listFiles({
 *   entity_type: 'product',
 *   entity_id: 123
 * })
 */
export async function listFiles(options = {}) {
  const {
    folder,
    entity_type,
    entity_id,
    limit = 50,
    offset = 0,
    include_deleted = false
  } = options

  const params = new URLSearchParams()
  params.append('limit', String(limit))
  params.append('offset', String(offset))
  if (folder) params.append('folder', folder)
  if (entity_type) params.append('entity_type', entity_type)
  if (entity_id) params.append('entity_id', String(entity_id))
  if (include_deleted) params.append('include_deleted', 'true')

  try {
    const response = await fetch(`${FILES_ENDPOINT}?${params.toString()}`, {
      method: 'GET',
      headers: getHeaders().headers
    })

    if (!response.ok) {
      const error = await response.json()
      throw new Error(error.error?.message || 'Failed to list files')
    }

    return await response.json()
  } catch (error) {
    console.error('listFiles error:', error)
    throw error
  }
}

/**
 * Delete a file
 *
 * @param {number} fileId - The file ID to delete
 * @param {boolean} hard - If true, permanently delete the file (default: soft delete)
 * @returns {Promise<Object>} Deletion result
 *
 * @example
 * // Soft delete (can be recovered)
 * await deleteFileById(123)
 *
 * // Hard delete (permanent)
 * await deleteFileById(123, true)
 */
export async function deleteFileById(fileId, hard = false) {
  try {
    const url = hard ? `${FILES_ENDPOINT}/${fileId}?hard=true` : `${FILES_ENDPOINT}/${fileId}`
    const response = await fetch(url, {
      method: 'DELETE',
      headers: getHeaders().headers
    })

    if (!response.ok) {
      const error = await response.json()
      throw new Error(error.error?.message || 'Failed to delete file')
    }

    return await response.json()
  } catch (error) {
    console.error('deleteFileById error:', error)
    throw error
  }
}

/**
 * Update file metadata (folder, entity association, public flag)
 *
 * @param {number} fileId - The file ID to update
 * @param {Object} data - Update data
 * @param {string} data.folder - New folder path
 * @param {string} data.entity_type - New entity type
 * @param {number} data.entity_id - New entity ID
 * @param {boolean} data.is_public - New public flag
 * @returns {Promise<Object>} Updated file metadata
 */
export async function updateFileMetadata(fileId, data) {
  try {
    const response = await fetch(`${FILES_ENDPOINT}/${fileId}`, {
      method: 'PATCH',
      headers: getHeaders().headers,
      body: JSON.stringify(data)
    })

    if (!response.ok) {
      const error = await response.json()
      throw new Error(error.error?.message || 'Failed to update file')
    }

    const result = await response.json()
    return result.resource || result
  } catch (error) {
    console.error('updateFileMetadata error:', error)
    throw error
  }
}

/**
 * Get files for a specific entity (convenience function)
 *
 * @param {string} entityType - Entity type (e.g., 'product', 'order', 'customer')
 * @param {number} entityId - Entity ID
 * @returns {Promise<Array>} Array of file records
 *
 * @example
 * const productImages = await getEntityFiles('product', 123)
 */
export async function getEntityFiles(entityType, entityId) {
  try {
    const result = await listFiles({
      entity_type: entityType,
      entity_id: entityId,
      limit: 100
    })
    return result.resource || []
  } catch (error) {
    console.error('getEntityFiles error:', error)
    return []
  }
}

/**
 * Upload multiple files at once
 *
 * @param {FileList|File[]} files - Array of files to upload
 * @param {Object} options - Upload options (same as uploadFile)
 * @param {Function} options.onFileProgress - Progress callback per file (fileIndex, percentage)
 * @returns {Promise<Array>} Array of uploaded file metadata
 *
 * @example
 * const results = await uploadMultipleFiles(fileList, {
 *   folder: '/products',
 *   entity_type: 'product',
 *   entity_id: 123,
 *   onFileProgress: (index, pct) => console.log(`File ${index}: ${pct}%`)
 * })
 */
export async function uploadMultipleFiles(files, options = {}) {
  const { onFileProgress, ...uploadOptions } = options
  const results = []

  for (let i = 0; i < files.length; i++) {
    const file = files[i]
    try {
      const result = await uploadFile(file, {
        ...uploadOptions,
        onProgress: onFileProgress ? (pct) => onFileProgress(i, pct) : null
      })
      results.push({ success: true, file: result })
    } catch (error) {
      results.push({ success: false, error: error.message, filename: file.name })
    }
  }

  return results
}

// ============================================================================
// END FILE STORAGE API
// ============================================================================

// ============================================================================
// PER-APP USER MANAGEMENT API
// ============================================================================
// These functions manage users, roles, and groups within a specific app's database
// Endpoint pattern: /api/v2/{appName}/system/{resource}
// ============================================================================

/**
 * Get base URL for app system endpoints
 * Uses the APP_NAME from api_urls.js (e.g., "flexflow/")
 */
function getAppSystemUrl(resource) {
  const appName = APP_NAME.replace(/\/$/, '') || 'flexflow'
  // Backend uses singular resource names: user, role, group
  return `${API_URL}${appName}/system/${resource}`
}

// ============================================================================
// APP USERS
// ============================================================================

/**
 * Get all users for the current app
 * @returns {Promise<Array>} Array of app users
 */
export async function getAppUsers() {
  try {
    const response = await fetch(getAppSystemUrl('user'), {
      method: "GET",
      headers: getHeaders().headers
    })

    if (!response.ok) {
      throw new Error("Failed to fetch users")
    }

    const data = await response.json()
    return data.resource || []
  } catch (error) {
    console.error("getAppUsers error:", error)
    throw error
  }
}

/**
 * Get a single app user by ID
 * @param {number} id - User ID
 * @returns {Promise<Object>} User data
 */
export async function getAppUserById(id) {
  try {
    const response = await fetch(`${getAppSystemUrl('user')}/${id}`, {
      method: "GET",
      headers: getHeaders().headers
    })

    // 404 = user not registered in this app tenant; benign for retail-only users
    // whose central login id doesn't have a matching app_user row. Return null
    // so callers can decide (they already do `if (user)`), and don't spam the
    // console with red errors.
    if (response.status === 404) return null

    if (!response.ok) {
      throw new Error("Failed to fetch user")
    }

    const data = await response.json()
    return data.resource?.[0] || data
  } catch (error) {
    console.error("getAppUserById error:", error)
    throw error
  }
}

/**
 * Get app users filtered by role ID
 * @param {number} roleId - Role ID to filter by
 * @returns {Promise<Array>} Array of users with the specified role
 */
export async function getAppUsersByRole(roleId) {
  try {
    const response = await fetch(`${getAppSystemUrl('user')}?role_id=${roleId}`, {
      method: "GET",
      headers: getHeaders().headers
    })

    if (!response.ok) {
      throw new Error("Failed to fetch users by role")
    }

    const data = await response.json()
    return data.resource || []
  } catch (error) {
    console.error("getAppUsersByRole error:", error)
    throw error
  }
}

/**
 * Create a new app user
 * @param {Object} userData - User data (email, password, name, role_id, etc.)
 * @returns {Promise<Object>} Created user
 */
export async function createAppUser(userData) {
  try {
    const response = await fetch(getAppSystemUrl('user'), {
      method: "POST",
      headers: getHeaders().headers,
      body: JSON.stringify({ resource: [userData] })
    })

    if (!response.ok) {
      const error = await response.json()
      throw new Error(error.error?.message || "Failed to create user")
    }

    const result = await response.json()
    return result.resource?.[0] || result
  } catch (error) {
    console.error("createAppUser error:", error)
    throw error
  }
}

/**
 * Update an existing app user
 * @param {number} id - User ID
 * @param {Object} userData - Updated user data
 * @returns {Promise<Object>} Updated user
 */
export async function updateAppUser(id, userData) {
  try {
    const response = await fetch(`${getAppSystemUrl('user')}/${id}`, {
      method: "PATCH",
      headers: getHeaders().headers,
      body: JSON.stringify(userData)
    })

    if (!response.ok) {
      const error = await response.json()
      throw new Error(error.error?.message || "Failed to update user")
    }

    const result = await response.json()
    return result.resource?.[0] || result
  } catch (error) {
    console.error("updateAppUser error:", error)
    throw error
  }
}

/**
 * Delete an app user
 * @param {number} id - User ID
 * @returns {Promise<boolean>} Success status
 */
export async function deleteAppUser(id) {
  try {
    const response = await fetch(`${getAppSystemUrl('user')}/${id}`, {
      method: "DELETE",
      headers: getHeaders().headers
    })

    if (!response.ok) {
      const error = await response.json()
      throw new Error(error.error?.message || "Failed to delete user")
    }

    return true
  } catch (error) {
    console.error("deleteAppUser error:", error)
    throw error
  }
}

// ============================================================================
// APP ROLES
// ============================================================================

/**
 * Get all roles for the current app
 * @returns {Promise<Array>} Array of roles
 */
export async function getAppRoles() {
  try {
    const response = await fetch(getAppSystemUrl('role'), {
      method: "GET",
      headers: getHeaders().headers
    })

    if (!response.ok) {
      throw new Error("Failed to fetch roles")
    }

    const data = await response.json()
    return data.resource || []
  } catch (error) {
    console.error("getAppRoles error:", error)
    throw error
  }
}

/**
 * Get a single role by ID
 * @param {number} id - Role ID
 * @returns {Promise<Object>} Role data
 */
export async function getAppRoleById(id) {
  try {
    const response = await fetch(`${getAppSystemUrl('role')}/${id}`, {
      method: "GET",
      headers: getHeaders().headers
    })

    if (!response.ok) {
      throw new Error("Failed to fetch role")
    }

    const data = await response.json()
    return data.resource?.[0] || data
  } catch (error) {
    console.error("getAppRoleById error:", error)
    throw error
  }
}

/**
 * Create a new role
 * @param {Object} roleData - Role data (name, description, permissions)
 * @returns {Promise<Object>} Created role
 */
export async function createAppRole(roleData) {
  try {
    const response = await fetch(getAppSystemUrl('role'), {
      method: "POST",
      headers: getHeaders().headers,
      body: JSON.stringify({ resource: [roleData] })
    })

    if (!response.ok) {
      const error = await response.json()
      throw new Error(error.error?.message || "Failed to create role")
    }

    const result = await response.json()
    return result.resource?.[0] || result
  } catch (error) {
    console.error("createAppRole error:", error)
    throw error
  }
}

/**
 * Update an existing role
 * @param {number} id - Role ID
 * @param {Object} roleData - Updated role data
 * @returns {Promise<Object>} Updated role
 */
export async function updateAppRole(id, roleData) {
  try {
    const response = await fetch(`${getAppSystemUrl('role')}/${id}`, {
      method: "PATCH",
      headers: getHeaders().headers,
      body: JSON.stringify(roleData)
    })

    if (!response.ok) {
      const error = await response.json()
      throw new Error(error.error?.message || "Failed to update role")
    }

    const result = await response.json()
    return result.resource?.[0] || result
  } catch (error) {
    console.error("updateAppRole error:", error)
    throw error
  }
}

/**
 * Delete a role
 * @param {number} id - Role ID
 * @returns {Promise<boolean>} Success status
 */
export async function deleteAppRole(id) {
  try {
    const response = await fetch(`${getAppSystemUrl('role')}/${id}`, {
      method: "DELETE",
      headers: getHeaders().headers
    })

    if (!response.ok) {
      const error = await response.json()
      throw new Error(error.error?.message || "Failed to delete role")
    }

    return true
  } catch (error) {
    console.error("deleteAppRole error:", error)
    throw error
  }
}

// ============================================================================
// APP MODULES (App-level enabled modules)
// ============================================================================

/**
 * Get enabled modules for the current app
 * @returns {Promise<Object>} Object with enabled_modules
 */
export async function getAppModules() {
  try {
    const response = await fetch(getAppSystemUrl('app-modules'), {
      method: "GET",
      headers: getHeaders().headers
    })

    if (!response.ok) {
      throw new Error("Failed to fetch app modules")
    }

    const data = await response.json()
    return data.enabled_modules || {}
  } catch (error) {
    console.error("getAppModules error:", error)
    throw error
  }
}

/**
 * Update enabled modules for the current app
 * @param {Object} enabledModules - Object mapping module keys to boolean
 * @returns {Promise<Object>} Updated enabled_modules
 */
export async function updateAppModules(enabledModules) {
  try {
    const response = await fetch(getAppSystemUrl('app-modules'), {
      method: "PATCH",
      headers: getHeaders().headers,
      body: JSON.stringify({ enabled_modules: enabledModules })
    })

    if (!response.ok) {
      const error = await response.json()
      throw new Error(error.error?.message || "Failed to update app modules")
    }

    const data = await response.json()
    return data.enabled_modules || {}
  } catch (error) {
    console.error("updateAppModules error:", error)
    throw error
  }
}

// ============================================================================
// APP GROUPS
// ============================================================================

/**
 * Get all groups for the current app
 * @returns {Promise<Array>} Array of groups
 */
export async function getAppGroups() {
  try {
    const response = await fetch(getAppSystemUrl('group'), {
      method: "GET",
      headers: getHeaders().headers
    })

    if (!response.ok) {
      throw new Error("Failed to fetch groups")
    }

    const data = await response.json()
    return data.resource || []
  } catch (error) {
    console.error("getAppGroups error:", error)
    throw error
  }
}

/**
 * Get a single group by ID with members
 * @param {number} id - Group ID
 * @returns {Promise<Object>} Group data with members
 */
export async function getAppGroupById(id) {
  try {
    const response = await fetch(`${getAppSystemUrl('group')}/${id}`, {
      method: "GET",
      headers: getHeaders().headers
    })

    if (!response.ok) {
      throw new Error("Failed to fetch group")
    }

    const data = await response.json()
    return data.resource?.[0] || data
  } catch (error) {
    console.error("getAppGroupById error:", error)
    throw error
  }
}

/**
 * Create a new group
 * @param {Object} groupData - Group data (name, description)
 * @returns {Promise<Object>} Created group
 */
export async function createAppGroup(groupData) {
  try {
    const response = await fetch(getAppSystemUrl('group'), {
      method: "POST",
      headers: getHeaders().headers,
      body: JSON.stringify({ resource: [groupData] })
    })

    if (!response.ok) {
      const error = await response.json()
      throw new Error(error.error?.message || "Failed to create group")
    }

    const result = await response.json()
    return result.resource?.[0] || result
  } catch (error) {
    console.error("createAppGroup error:", error)
    throw error
  }
}

/**
 * Update an existing group
 * @param {number} id - Group ID
 * @param {Object} groupData - Updated group data
 * @returns {Promise<Object>} Updated group
 */
export async function updateAppGroup(id, groupData) {
  try {
    const response = await fetch(`${getAppSystemUrl('group')}/${id}`, {
      method: "PATCH",
      headers: getHeaders().headers,
      body: JSON.stringify(groupData)
    })

    if (!response.ok) {
      const error = await response.json()
      throw new Error(error.error?.message || "Failed to update group")
    }

    const result = await response.json()
    return result.resource?.[0] || result
  } catch (error) {
    console.error("updateAppGroup error:", error)
    throw error
  }
}

/**
 * Delete a group
 * @param {number} id - Group ID
 * @returns {Promise<boolean>} Success status
 */
export async function deleteAppGroup(id) {
  try {
    const response = await fetch(`${getAppSystemUrl('group')}/${id}`, {
      method: "DELETE",
      headers: getHeaders().headers
    })

    if (!response.ok) {
      const error = await response.json()
      throw new Error(error.error?.message || "Failed to delete group")
    }

    return true
  } catch (error) {
    console.error("deleteAppGroup error:", error)
    throw error
  }
}

/**
 * Get members of a group
 * @param {number} groupId - Group ID
 * @returns {Promise<Array>} Array of group members
 */
export async function getAppGroupMembers(groupId) {
  try {
    const response = await fetch(`${getAppSystemUrl('group')}/${groupId}/member`, {
      method: "GET",
      headers: getHeaders().headers
    })

    if (!response.ok) {
      throw new Error("Failed to fetch group members")
    }

    const data = await response.json()
    return data.resource || []
  } catch (error) {
    console.error("getAppGroupMembers error:", error)
    throw error
  }
}

/**
 * Add a member to a group
 * @param {number} groupId - Group ID
 * @param {number} userId - User ID to add
 * @returns {Promise<Object>} Result
 */
export async function addAppGroupMember(groupId, userId) {
  try {
    const response = await fetch(`${getAppSystemUrl('group')}/${groupId}/member`, {
      method: "POST",
      headers: getHeaders().headers,
      body: JSON.stringify({ user_id: userId })
    })

    if (!response.ok) {
      const error = await response.json()
      throw new Error(error.error?.message || "Failed to add group member")
    }

    return await response.json()
  } catch (error) {
    console.error("addAppGroupMember error:", error)
    throw error
  }
}

/**
 * Remove a member from a group
 * @param {number} groupId - Group ID
 * @param {number} userId - User ID to remove
 * @returns {Promise<boolean>} Success status
 */
export async function removeAppGroupMember(groupId, userId) {
  try {
    const response = await fetch(`${getAppSystemUrl('group')}/${groupId}/member/${userId}`, {
      method: "DELETE",
      headers: getHeaders().headers
    })

    if (!response.ok) {
      const error = await response.json()
      throw new Error(error.error?.message || "Failed to remove group member")
    }

    return true
  } catch (error) {
    console.error("removeAppGroupMember error:", error)
    throw error
  }
}

// ============================================================================
// END PER-APP USER MANAGEMENT API
// ============================================================================

// export async function put(url, data, config = {}) {
//   return axiosApi
//     .put(url, { ...data }, { ...config })
//     .then(response => response.data)
// }

// export async function del(url, config = {}) {
//   return await axiosApi
//     .delete(url, { ...config })
//     .then(response => response.data)
// }
export default {getCustomerInfo,getSalesPersonsCustomers,getPayments,postPayment, getOrdersbystatus,postProvider, getCodes, getLoans, postLoan,getTasksby,getOrdersbyid,totalCost, patchOrder,patchCustomer,patchTask ,checkOrderStock,getCustomers, postCustomer, postProduct, getMaterials, postMaterial, patchProduct, calccost ,getCustOrders,patchMaterial}